import { Component, DestroyRef, inject, effect, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CdkDragDrop, CdkDropList, CdkDrag, CdkDragHandle, CdkDragPlaceholder, moveItemInArray } from '@angular/cdk/drag-drop';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { CanComponentDeactivate } from 'src/app/guards/can-deactivate.guard';
import { AppStateService } from 'src/app/shared/services/helpers/app-state.service';
import * as _ from 'lodash';
import * as moment from 'moment';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SettingsService } from 'src/app/shared/services/settings';
import { AccountsService } from 'src/app/shared/services/accounts/accounts.service';
import { ActionService } from 'src/app/shared/services/action/action.service';
import { DynamicActionFormRendererComponent } from 'src/app/shared/components/dynamic-action-form-renderer/dynamic-action-form-renderer.component';
import { LtmsLoaderComponent } from 'src/app/shared/components/ltms-loader/ltms-loader.component';
import { dataFieldRuleSchema } from '../../constants/schemas';
// Declare Helpers globally
declare const Helpers: any;

interface DataField {
  field_id: string;
  display_name: string;
  name?: string;
  sequence_no?: number;
  visible?: boolean;
  read_only?: boolean;
  mandatory?: boolean;
  custom_parameter_type?: number;
  group_id?: string;
  children?: DataField[];
  is_deleted?: boolean;
  hide_delete?: boolean;
  hide_newField?: boolean;
  isParent?: boolean;
  isChild?: boolean;
  hasChildren?: boolean;
  parentId?: string;
  isLastChild?: boolean;
  isNewFieldButton?: boolean;
  parentField?: DataField;
  always_open?: boolean;
  display_in?: string;
  user_upload_type?: string;
  access_key_rules?: any[];
}

interface Rule {
  id: string;
  name: string;
  description: string;
  active: boolean;
  is_enabled: boolean;
  created_date: Date;
  last_updated_on: Date;
  updated_by?: string;
  formatted_created_date?: string;
  formatted_last_updated?: string;
  field_id_list?: string[];
  conditions?: any;
  type?: string;
  is_deleted?: boolean;
}


@Component({
  selector: 'app-litmus-action-data-fields',
  templateUrl: './litmus-action-data-fields.component.html',
  styleUrls: ['./litmus-action-data-fields.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, DynamicActionFormRendererComponent, CdkDropList, CdkDrag, CdkDragHandle, CdkDragPlaceholder, LtmsLoaderComponent]
})
export class LitmusActionDataFieldsComponent implements OnInit, CanComponentDeactivate {
  @Input() readonly: boolean = true;

  destroyRef = inject(DestroyRef);

  // Settings and view mode
  viewMode: 'basic' | 'advanced' = 'basic';

  // Matching old staticReservedAccordions (sequence_no 0-3 reserved)
  private readonly staticReservedAccordions: any[] = [{
    field_id: 'parameter',
    display_name: 'Parameter Ratings',
    sequence_no: 0,
    visibile: true,
    read_only: false,
    name: 'Parameter Ratings',
    hide_delete: true,
    hide_newField: true
  }, {
    field_id: 'chat',
    display_name: 'Chat',
    sequence_no: 1,
    visibile: true,
    read_only: false,
    name: 'Chat',
    hide_delete: true,
    hide_newField: true
  }, {
    field_id: 'profile',
    display_name: 'Profile',
    sequence_no: 2,
    visibile: true,
    read_only: false,
    name: 'Profile',
    hide_delete: true,
    hide_newField: true
  }, {
    field_id: 'transaction',
    display_name: 'Transaction',
    sequence_no: 3,
    visibile: true,
    read_only: false,
    name: 'Transaction',
    hide_delete: true,
    hide_newField: true
  }];

  // Loading and state management
  pagerBusy = false;
  isSaving = false;
  isCancelling = false;
  isSaveEnabled = false;

  // Brand and configuration
  brandId: string = '';
  brandDetails: any = {};
  configurationLoaded: boolean = false;
  hierarchyMapping: any = {};
  tableRows: any = [];

  // Modal state for groups
  isModalOpen = false;
  editMode = false;
  selectedGroupDetails: any = {};
  modalSchema: any = {};
  defaultValues: any = {};
  // Track previous group values for change detection (matching old dashboard)
  private previousFieldName: string = '';
  private openBooleanField: any = null;
  private previousDisplayIn: any = null;
  private previousUploadUserType: any = null;
  private addGroupFlag: boolean = false;

  // Modal state for child fields
  isChildFieldModalOpen = false;
  childFieldEditMode = false;
  selectedChildFieldDetails: any = {};
  childFieldDefaultValues: any = {};
  currentParentFieldId: string = ''; // Parent group ID when adding/editing child field

  // Modal state for rules
  isRuleModalOpen = false;
  ruleEditMode = false;
  selectedRuleDetails: any = {};

  ruleModalSchema: any = {};
  ruleDefaultValues: any = {};

  // Workflow rules data
  workflowFields: any[] = [];
  workflowOperators: any[] = [];
  rulesetHash: any = {};
  fieldDefsMap: any = {};
  ruleSetIdsToExclude: string[] = ['lw_event_fields_logic_ruleset', 'do_api_call_to_check_for_cust_id'];

  // Data field options for "Applies To"
  appliesToOptions: any[] = [];
  appliesToChildFieldOptions: any[] = [];
  ltmsEventList: any[] = [];
  dataRuleType: string = '';

  // Data storage
  dataFieldHash: any = {};
  dataFieldBackup: any = {};
  dataFieldList: DataField[] = [];
  dataFieldListBackup: DataField[] = [];
  filteredFields: DataField[] = [];
  groupedDataFields: DataField[] = []; // For card-based display

  dataFieldRules: Rule[] = [];
  dataFieldRulesBackup: Rule[] = [];
  filteredRules: Rule[] = [];

  // Search functionality
  searchText: string = '';
  textToSearch: string = '';

  // Filter state variables
  t1: number = 1; // Flag to hold type state
  oldType: string | number = ""; // Hold type when changing page
  indexFrom: number = 0;
  filteredData: any[] = [];

  // Pagination for rules (matching old dashboard structure)
  rulesPageNo: number = 1;
  limit: number = 10;
  rulesPageFrom: number = 1;
  rulesPageTo: number = 1;
  rulesCount: number = 0;
  disableNext: boolean = true;
  disablePrev: boolean = true;
  isNextCtrl: boolean = true;

  // Filter state
  selectedRuleTypeFilter: number = 0; // 0 = All, 1 = Visibility, 2 = Read-only, 3 = Mandatory
  filterOrAll: number = 0; // 0 = all, 1 = filtered

  // Modal schema for data group
  dataGroupModalSchema = {
    title: 'Data Group',
    type: 'object',
    properties: {
      name: {
        label: 'Group Name',
        type: 'string',
        required: true,
        minLength: 3,
        placeholder: 'Group Name'
      },
      always_open: {
        label: 'Always Open',
        type: 'boolean',
        required: false,
        component: 'switch'
      },
      display_in: {
        label: 'Display in',
        type: 'string',
        component: 'button-group',
        options: [
          { label: 'Default Action Items', value: 'default' },
          { label: 'Custom Action Items', value: 'custom' }
        ],
        default: 'default',
      },
      user_upload_type: {
        label: 'Toggle Read/Write Access For',
        type: 'string',
        component: 'dropdown',
        options: [
          { label: 'Users added via Hierarchy Upload', value: 'afp_users' },
          { label: 'Users added via Dashboard', value: 'manual_managed_users' }
        ],
        textField: 'label',
        valueField: 'value',
        required: true, 
        placeholder: 'Select user type',
        errorMessages: {
          required: 'Upload User Type is mandatory.'
        }
      },
      levels: {
        label: 'Set Level Based Visibility',
        type: 'table',
        columns: [
          {
            label: 'Organisation Levels',
            key: 'label',
            type: "label"
          },
          {
            label: 'Visibility',
            key: 'visibility',
            type: "checkbox"
          },
          {
            label: 'Write Access',
            key: 'writeAccess',
            type: "checkbox"
          }
        ],
        errorMessages: {
          required: 'Please select at least one organisation level'
        }
      }
    }
  };

  dataGroupModalDefaultValues = {
    name: '',
    always_open: false,
    display_in: 'default',
    user_upload_type: '',
    levels: []
  };

  // Child field modal schema — matching old addChild/editChild Basic + Advanced tabs
  // and getOptionsBasedOnType (attachment/number/string/date/datetime/list/link)
  private childFieldModalSchemaBase: any = null;
  childFieldModalSchema: any = {
    title: 'Data Field',
    type: 'object',
    schema_type: 'data_field_child',
    basic: {
      properties: {
        field_name: {
          label: 'Field Name',
          type: 'string',
          required: true,
          minLength: 1,
          maxLength: 30,
          placeholder: 'Field Name',
          errorMsg: 'Field Name is mandatory.'
        },
        display_name: {
          label: 'Display Name',
          type: 'string',
          required: true,
          minLength: 1,
          maxLength: 30,
          placeholder: 'Display Name',
          errorMsg: 'Display Name is mandatory.'
        },
        data_category: {
          label: 'Data Category',
          type: 'string',
          component: 'dropdown',
          required: true,
          options: [
            { id: 'profile_data', name: 'Profile Data' },
            { id: 'transactional_data', name: 'Transactional Data' },
            { id: 'request_data', name: 'Request Data' },
            { id: 'interaction_data', name: 'Interaction Data' },
            { id: 'action_data', name: 'Action Data' }
          ],
          textField: 'name',
          valueField: 'id',
          placeholder: 'Select Data Category',
          errorMsg: 'Data Category is mandatory.'
        },
        field_type: {
          label: 'Field Type',
          type: 'string',
          component: 'dropdown',
          required: true,
          options: [
            { id: 'attachment', name: 'Attachment' },
            { id: 'number', name: 'Number' },
            { id: 'string', name: 'String' },
            { id: 'date', name: 'Date' },
            { id: 'list', name: 'List' },
            { id: 'datetime', name: 'Date and Time' },
            { id: 'link', name: 'Link' }
          ],
          textField: 'name',
          valueField: 'id',
          placeholder: 'Select Field Type',
          errorMsg: 'Field Type is mandatory.',
          note: ''
        },
        // Attachment note (shown via field_type.note when type=attachment — also standalone description)
        attachment_note: {
          label: 'Note',
          type: 'string',
          description: 'Please Note: Maximum of 5 files can be uploaded and the size of each file can not exceed 3MB.',
          component: 'label',
          hidden: true,
          condition: {
            parentField: 'field_type',
            operator: 'eq',
            value: 'attachment',
            fieldToUpdate: 'hidden',
            valueToSet: false
          }
        },
        // Number
        min_value: {
          label: 'Minimum Value',
          type: 'string',
          placeholder: 'Enter minimum value',
          hidden: true,
          condition: {
            parentField: 'field_type',
            operator: 'eq',
            value: 'number',
            fieldToUpdate: 'hidden',
            valueToSet: false
          }
        },
        max_value: {
          label: 'Maximum Value',
          type: 'string',
          placeholder: 'Enter maximum value',
          hidden: true,
          condition: {
            parentField: 'field_type',
            operator: 'eq',
            value: 'number',
            fieldToUpdate: 'hidden',
            valueToSet: false
          }
        },
        // String
        allow_multiline: {
          label: 'Allow Multi-line',
          type: 'boolean',
          component: 'switch',
          default: false,
          hidden: true,
          condition: {
            parentField: 'field_type',
            operator: 'eq',
            value: 'string',
            fieldToUpdate: 'hidden',
            valueToSet: false
          }
        },
        textbox_height: {
          label: 'Text Box Height',
          type: 'string',
          placeholder: 'Text box height (in rows)',
          hidden: true,
          condition: {
            parentField: 'field_type',
            operator: 'eq',
            value: 'string',
            fieldToUpdate: 'hidden',
            valueToSet: false
          }
        },
        max_characters: {
          label: 'Maximum Character(s)',
          type: 'string',
          placeholder: 'Maximum Characters',
          hidden: true,
          condition: {
            parentField: 'field_type',
            operator: 'eq',
            value: 'string',
            fieldToUpdate: 'hidden',
            valueToSet: false
          }
        },
        // Date
        date_format: {
          label: 'Date Format',
          type: 'string',
          component: 'dropdown',
          required: false,
          options: [
            { id: 'mm-dd-yy', name: 'MM-DD-YY' },
            { id: 'dd-mm-yy', name: 'DD-MM-YY' },
            { id: 'yy-mm-dd', name: 'YY-MM-DD' },
            { id: 'MM d, yy', name: 'Month D, Yr' }
          ],
          textField: 'name',
          valueField: 'id',
          placeholder: 'Date format such as - DD/MM/YY',
          errorMsg: 'Date Format is required.',
          hidden: true,
          condition: {
            parentField: 'field_type',
            operator: 'eq',
            value: 'date',
            fieldToUpdate: 'hidden',
            valueToSet: false
          }
        },
        // Link
        link_urls: {
          label: 'URL',
          type: 'string',
          required: false,
          placeholder: 'URL',
          maxLength: 1000,
          errorMsg: 'URL is required.',
          hidden: true,
          condition: {
            parentField: 'field_type',
            operator: 'eq',
            value: 'link',
            fieldToUpdate: 'hidden',
            valueToSet: false
          }
        },
        // List — Individual / API (matching old list component primary modes)
        list_data_source: {
          label: 'Data Source',
          type: 'string',
          component: 'button-group',
          options: [
            { label: 'Individual Values', value: 'individual' },
            { label: 'Data Mapping', value: 'mapping' },
            { label: 'API', value: 'api' }
          ],
          default: 'individual',
          hidden: true,
          condition: {
            parentField: 'field_type',
            operator: 'eq',
            value: 'list',
            fieldToUpdate: 'hidden',
            valueToSet: false
          }
        },
        list_individual_values: {
          label: 'Values',
          type: 'string',
          placeholder: 'Comma separated values',
          description: 'Note: Separate with comma for multiple values.',
          hidden: true,
          condition: {
            parentField: 'field_type',
            operator: 'eq',
            value: 'list',
            fieldToUpdate: 'hidden',
            valueToSet: false
          }
        },
        list_dataset_id: {
          label: 'Data Set Id',
          type: 'string',
          placeholder: 'Dataset id from Data Mapping',
          hidden: true,
          condition: {
            parentField: 'field_type',
            operator: 'eq',
            value: 'list',
            fieldToUpdate: 'hidden',
            valueToSet: false
          }
        },
        list_parent_field_id: {
          label: 'Parent Field Column (optional)',
          type: 'string',
          component: 'dropdown',
          options: [],
          textField: 'name',
          valueField: 'id',
          placeholder: 'Select column',
          hidden: true,
          condition: {
            parentField: 'field_type',
            operator: 'eq',
            value: 'list',
            fieldToUpdate: 'hidden',
            valueToSet: false
          }
        },
        list_api_method: {
          label: 'Method',
          type: 'string',
          component: 'button-group',
          options: [
            { label: 'GET', value: 'GET' },
            { label: 'POST', value: 'POST' }
          ],
          default: 'GET',
          hidden: true,
          condition: {
            parentField: 'field_type',
            operator: 'eq',
            value: 'list',
            fieldToUpdate: 'hidden',
            valueToSet: false
          }
        },
        list_api_url: {
          label: 'URL',
          type: 'string',
          placeholder: 'API Url',
          hidden: true,
          condition: {
            parentField: 'field_type',
            operator: 'eq',
            value: 'list',
            fieldToUpdate: 'hidden',
            valueToSet: false
          }
        },
        list_api_value_field: {
          label: 'Value Field',
          type: 'string',
          placeholder: 'Value field key',
          hidden: true,
          condition: {
            parentField: 'field_type',
            operator: 'eq',
            value: 'list',
            fieldToUpdate: 'hidden',
            valueToSet: false
          }
        },
        list_api_display_field: {
          label: 'Display Field',
          type: 'string',
          placeholder: 'Display field key',
          hidden: true,
          condition: {
            parentField: 'field_type',
            operator: 'eq',
            value: 'list',
            fieldToUpdate: 'hidden',
            valueToSet: false
          }
        }
      }
    },
    advanced: {
      properties: {
        mandatory: {
          label: 'Mandatory',
          type: 'boolean',
          component: 'switch',
          default: true,
          hidden: false,
          condition: {
            parentField: 'field_type',
            operator: 'eq',
            value: 'link',
            fieldToUpdate: 'hidden',
            valueToSet: true
          }
        },
        allow_multiple: {
          label: 'Allow Multiple',
          type: 'boolean',
          component: 'switch',
          default: false,
          hidden: false,
          condition: {
            parentField: 'field_type',
            operator: 'in',
            value: ['attachment', 'link'],
            fieldToUpdate: 'hidden',
            valueToSet: true
          }
        },
        read_only: {
          label: 'Read-Only',
          type: 'boolean',
          component: 'switch',
          default: false,
          hidden: false,
          condition: {
            parentField: 'field_type',
            operator: 'in',
            value: ['attachment', 'link', 'date', 'datetime'],
            fieldToUpdate: 'hidden',
            valueToSet: true
          }
        },
        download_enabled: {
          label: 'Download Enabled',
          type: 'boolean',
          component: 'switch',
          default: false,
          hidden: false,
          condition: {
            parentField: 'field_type',
            operator: 'in',
            value: ['attachment', 'link', 'date', 'datetime'],
            fieldToUpdate: 'hidden',
            valueToSet: true
          }
        },
        add_to_filters: {
          label: 'Add to Filters',
          type: 'boolean',
          component: 'switch',
          default: false,
          hidden: false,
          condition: {
            parentField: 'field_type',
            operator: 'any',
            rules: [
              { operator: 'in', value: ['attachment', 'link', 'date', 'datetime'] },
              { operator: 'starts_with', value: 'ltms_custom_field_' }
            ],
            fieldToUpdate: 'hidden',
            valueToSet: true
          }
        },
        add_to_cohorts: {
          label: 'Add to Cohorts',
          type: 'boolean',
          component: 'switch',
          default: false,
          hidden: false,
          condition: {
            parentField: 'field_type',
            operator: 'any',
            rules: [
              { operator: 'in', value: ['attachment', 'link', 'date', 'datetime'] },
              { operator: 'starts_with', value: 'ltms_custom_field_' }
            ],
            fieldToUpdate: 'hidden',
            valueToSet: true
          }
        },
        inbox_search_enabled: {
          label: 'Inbox Search Enabled',
          type: 'boolean',
          component: 'switch',
          default: false,
          hidden: false,
          condition: {
            parentField: 'field_type',
            operator: 'any',
            rules: [
              { operator: 'in', value: ['attachment', 'link', 'date', 'datetime'] },
              { operator: 'starts_with', value: 'ltms_custom_field_' }
            ],
            fieldToUpdate: 'hidden',
            valueToSet: true
          }
        },
        show_field_label: {
          label: 'Show Field Label',
          type: 'boolean',
          component: 'switch',
          default: false,
          hidden: false,
          condition: {
            parentField: 'field_type',
            operator: 'starts_with',
            value: 'ltms_custom_field_',
            fieldToUpdate: 'hidden',
            valueToSet: true
          }
        },
        show_in_inbox_detail: {
          label: 'Show in Inbox detail',
          type: 'boolean',
          component: 'switch',
          default: true
        },
        restrict_backdating: {
          label: 'Restrict Backdating',
          type: 'boolean',
          component: 'switch',
          default: false,
          hidden: true,
          condition: {
            parentField: 'field_type',
            operator: 'in',
            value: ['date', 'datetime'],
            fieldToUpdate: 'hidden',
            valueToSet: false
          }
        }
      }
    },
    sections: [
      { name: 'Basic', type: 'basic' },
      { name: 'Advanced', type: 'advanced' }
    ]
  };

  childFieldModalDefaultValues = {
    field_name: '',
    display_name: '',
    data_category: '',
    field_type: '',
    min_value: '',
    max_value: '',
    allow_multiline: false,
    textbox_height: '',
    max_characters: '',
    date_format: '',
    link_urls: '',
    list_data_source: 'individual',
    list_individual_values: '',
    list_dataset_id: '',
    list_parent_field_id: '',
    list_api_method: 'GET',
    list_api_url: '',
    list_api_value_field: '',
    list_api_display_field: '',
    mandatory: true,
    allow_multiple: false,
    read_only: false,
    download_enabled: false,
    add_to_filters: false,
    add_to_cohorts: false,
    inbox_search_enabled: false,
    show_field_label: false,
    show_in_inbox_detail: true,
    restrict_backdating: false
  };
  pagingButtons: any = {
    isNext: false,
    isPrevious: false,
    isLast: false,
    isFirst: false
  }
  pagingDetails: any = {
    start: 0,
    end: 0,
    pageSize: 10,
    pageNumber: 1,
    total: 0,
    totalpages: 1
  }
  isDataLoaded: boolean = true;
  filterDataFieldRules: any[] = [];
  sortDetailForRules: any = {}
  lwDataFieldWorkflowActionsList: number = 0;
  constructor(
    private toastService: ToastService,
    private _settingsService: SettingsService,
    private stateService: AppStateService,
    private accountsService: AccountsService,
    private actionService: ActionService
  ) {
    effect(() => {
      if (this.stateService.initialized()) {
        const brandDetail: any = this.stateService.brandDetails();
        if (!_.isEmpty(brandDetail)) {
          if (this.brandId && this.brandId === brandDetail._id && this.configurationLoaded) {
            this.brandDetails = brandDetail;
            this.refreshDataFieldRules();
            return;
          }
          this.brandDetails = brandDetail;
          this.brandId = brandDetail._id;
          this.onChange();
          this.configurationLoaded = true;
          // Prepare hierarchy mapping when brand details are loaded
          this.prepareHierarchyMapping();
          this.getDataFields(this.brandDetails.field_definitions).then(() => {
            this.prepareDataFieldRulesModal();
            this.getLtmsEventList().then(() => {
              this.refreshDataFieldRules();
            });
          });
        }
      }
    })
  }

  ngOnInit() {
    this.resetData();
    // Don't call prepareHierarchyMapping here - it will be called when brandDetails are loaded in effect()
    this.onChange();
  }


  paginateRulesList(pagingType: string) {
    this.paginateFeedList(pagingType);
    this.getRulePageWises();
    this.getRulefilterWises();
  }
  paginateFeedList(pagingType: string) {
    this.pagingDetails.pageNumber = this.pagingDetails.pageNumber + (pagingType === 'next' ? 1 : -1);
    this.pagingDetails.start = (this.pagingDetails.pageNumber - 1) * this.pagingDetails.pageSize;
    this.pagingDetails.end = this.pagingDetails.start + this.pagingDetails.pageSize;
  }

  /**
   * getManagerIds: Method to get the manager id  from field defination rules and create an array.
   * @param  {Array} field_definitions       : field defination of brand.
   */
  getManagerIds(field_definitions) {
    var managerIds: any = [];
    _.each(field_definitions, (field: any) => {
      if (field && field.rules && field.rules.length) {
        _.each(field.rules, (rule: any) => {
          if (rule && rule.last_updated_by) {
            managerIds.push(rule.last_updated_by)
          }
        })
      }
    });
    return (managerIds && managerIds.length) ? _.uniq(managerIds) : [];
  }

  /**
   * Rebuild the rules list from the current brand details without resetting the tab.
   */
  refreshDataFieldRules() {
    const fieldDefinitions = _.get(this.brandDetails, "field_definitions");
    const managerIds: any = this.getManagerIds(fieldDefinitions);
    const applyRules = (managers: any[]) => {
      this.prepareDataFieldRules(
        _.get(this.brandDetails, "ui_rules"),
        fieldDefinitions,
        _.get(this.brandDetails, "accordions_list"),
        managers
      );
    };
    if (managerIds && managerIds.length) {
      this._settingsService.getManagerLists("managers", managerIds).subscribe((response: any) => {
        const managers = (response && response.code == 0 && response.managers && response.managers.length) ? response.managers : [];
        applyRules(managers);
      }, () => {
        applyRules([]);
      });
    } else {
      applyRules([]);
    }
  }

  /**
         * prepareDataFieldRules            : Method to prepare Rules for Data field.
         *                                    This is stored in brand collection with key 'ui_rules'
         * @param  {array} rules            : Array of objects. Each object contains 'event_name' and 'workflow'. 'workflow' further contains conditions and actions
         * @param  {array} fieldDefinitions : Array of objects. Field definitions within brands.
         */

  prepareDataFieldRules(ui_rules: any, fieldDefinitions: any, accordionsList: any, managers: any) {
    var dataFieldRules: any = [];
    var rulesInAccordionsAndField: any = [];
    var managersMapping = {};
    if (ui_rules && ui_rules.length) {
      dataFieldRules = Helpers.deepClone(ui_rules);
    }

    _.each(managers, function (manager) {
      if (!_.isEmpty(manager)) {
        managersMapping[manager._id] = manager
      }
    })

    if (fieldDefinitions && fieldDefinitions.length) {
      var rulesWithInFieldDefinitions: any = [];
      _.each(fieldDefinitions, function (definition) {
        if (definition.rules) {
          // Update field definition's field Id in each rule. This will be further used when we update the rule
          _.each(definition.rules, function (rule) { rule.applies_to = definition.group_id; }); //changing applies_to to parent_id as we will never have rules at parent level going forward
          // Merge the rules which are in field definitions 
          rulesWithInFieldDefinitions = _.flatten([rulesWithInFieldDefinitions, (definition.rules || [])]);
        }
      })
      rulesWithInFieldDefinitions = _.uniqBy(rulesWithInFieldDefinitions, "uuid")

      if (accordionsList && accordionsList.length) {
        var rulesWithInStaticAccordions: any = [];
        _.each(accordionsList, function (accordionList) {
          if (accordionList.rules) {
            _.each(accordionList.rules, function (rule) { rule.applies_to = accordionList.field_id; });
            // Merge the rules which are in field definitions
            rulesWithInStaticAccordions = _.flatten([rulesWithInStaticAccordions, (accordionList.rules || [])]);
          }
        })
        rulesWithInFieldDefinitions = rulesWithInFieldDefinitions.concat(rulesWithInStaticAccordions);
      }

      // rulesWithInFieldDefinitions = Object.assign(rulesWithInFieldDefinitions, rulesWithInStaticAccordions);
      // Merge rules within ui_rules(mandatory) and rules within field_definitions(visibility/read-only)
      dataFieldRules = _.flatten([dataFieldRules, rulesWithInFieldDefinitions]);
    }
    // Filter out the rules which are deleted
    dataFieldRules = _.filter(dataFieldRules, function (rule) {
      // Ignore the rules which are deleted.
      return (_.get(rule, "is_deleted") != true);
    });

    // looping through each rule and format created_date and last_updated_on.
    _.each(dataFieldRules, function (rule, idx) {
      if (!rule.last_updated_on) {
        rule.last_updated_on = rule.created_date;
      }
      dataFieldRules[idx].formatted_created_date = moment(rule.created_date).format("DD-MMM-YY HH:mm");
      dataFieldRules[idx].formatted_last_updated = moment(rule.last_updated_on).format("DD-MMM-YY HH:mm");
      if (managersMapping[rule.last_updated_by]) {
        if (managersMapping[rule.last_updated_by].name && managersMapping[rule.last_updated_by].name != "") {
          dataFieldRules[idx].formatted_last_updated = dataFieldRules[idx].formatted_last_updated + " by " + managersMapping[rule.last_updated_by].name;
        } else if (managersMapping[rule.last_updated_by].email_address && managersMapping[rule.last_updated_by].email_address != "") {
          dataFieldRules[idx].formatted_last_updated = dataFieldRules[idx].formatted_last_updated + " by " + managersMapping[rule.last_updated_by].email_address;
        }
      }
    })
    dataFieldRules = _.compact(dataFieldRules)
    dataFieldRules = _.sortBy(dataFieldRules, function (rule) {
      return new Date(rule.created_date).getTime();
    })

    this.dataFieldRulesBackup = Helpers.deepClone(dataFieldRules);
    this.filterDataFieldRules = Helpers.deepClone(dataFieldRules);

    // Prepare Sort Detail: Will be used in lw-table-renderer.component
    this.sortDetailForRules = { 'sort_by': 'date', 'field_name': 'created_date', 'is_sort_in_asc': false };
    this.pagingDetails.total = (_.get(this, "dataFieldRulesBackup")) ? this.dataFieldRulesBackup.length : 0;

    // Reset filter state
    this.selectedRuleTypeFilter = 0;
    this.textToSearch = '';
    this.searchText = '';
    this.filteredData = [];
    this.pagingDetails.pageNumber = 1;
    this.oldType = "";
    this.pagingDetails.start = 0;

    // Initialize pagination
    this.getRulePageWises();
  }


  /*

  getRulePageWises : This function provide the rule count according to pagination 
                    : It uses the filterDataFieldRules
                    : This function call call to dataSetCtrl.getRules() function for change the page.

  */
  // Filtered rules array

  /**
   * Get rule page wise data (pagination) - for "All" filter type
   * This function provides the rule count according to pagination
   * It uses the filterDataFieldRules
   */
  getRulePageWises() {
    const ruleList = _.cloneDeep(this.filterDataFieldRules);

    // Reset to page 1 if filtered list length equals limit
    if (this.filterDataFieldRules && (this.pagingDetails.pageSize === this.filterDataFieldRules.length)) {
      this.pagingDetails.pageNumber = 1;
    }

    this.pagingDetails.start = ((this.pagingDetails.pageNumber - 1) * this.pagingDetails.pageSize) + 1;
    this.pagingDetails.end = this.pagingDetails.start + this.pagingDetails.pageSize;
    this.rulesCount = (ruleList && ruleList.length) ? ruleList.length : 0;

    // Get the rules from indexFrom to limit
    this.dataFieldRules = ruleList.splice(this.pagingDetails.start - 1, this.pagingDetails.pageSize);
    const workflowLength = this.dataFieldRules.length || 0;

    if (workflowLength && workflowLength === this.pagingDetails.pageSize) {
      // If the fetched rule equals the limit
      this.pagingDetails.start = ((this.pagingDetails.pageNumber - 1) * this.limit) + 1;
      this.pagingDetails.end = this.pagingDetails.start + this.pagingDetails.pageSize;
      this.pagingButtons.isNext = (this.pagingDetails.pageNumber > 1);
      this.pagingButtons.isPrevious = (this.pagingDetails.pageNumber < this.pagingDetails.totalpages);
      this.pagingButtons.isLast = (this.pagingDetails.pageNumber < this.pagingDetails.totalpages);
      this.pagingButtons.isFirst = (this.pagingDetails.pageNumber > 1);
    } else if (workflowLength < this.pagingDetails.pageSize && workflowLength !== 0) {
      // If the fetched rule is less than the limit
      this.pagingDetails.start = ((this.pagingDetails.pageNumber - 1) * this.pagingDetails.pageSize) + 1;
      this.pagingDetails.end = workflowLength;
      this.pagingButtons.isNext = false;
      this.pagingButtons.isPrevious = (this.pagingDetails.pageNumber > 1);
      this.pagingButtons.isLast = (this.pagingDetails.pageNumber < this.pagingDetails.totalpages);
      this.pagingButtons.isFirst = (this.pagingDetails.pageNumber > 1);
    }
  }


  /**
   * Get rule filter wise data (pagination) - for filtered types (Visibility, Read-only, Mandatory)
   * This function provides the rule count according to pagination when filter is applied
   */
  getRulefilterWises() {
    const ruleList = _.cloneDeep(this.filterDataFieldRules);

    // Reset to page 1 if filtered list length equals limit
    if (this.filterDataFieldRules && (this.pagingDetails.pageSize === this.filterDataFieldRules.length)) {
      this.pagingDetails.pageNumber = 1;
    }

    this.filterOrAll = 1;
    this.pagingDetails.start = ((this.pagingDetails.pageNumber - 1) * this.pagingDetails.pageSize) + 1;

    // Get the rules from indexFrom to limit
    this.dataFieldRules = ruleList.splice(this.pagingDetails.start - 1, this.pagingDetails.pageSize);
    const workflowLength = this.dataFieldRules.length || 0;

    if (workflowLength && workflowLength === this.limit) {
      // If the fetched rule equals the limit
      this.pagingDetails.start = ((this.pagingDetails.pageNumber - 1) * this.pagingDetails.pageSize) + 1;
      this.pagingDetails.end = this.pagingDetails.start + this.pagingDetails.pageSize;
      this.pagingButtons.isNext = (this.pagingDetails.pageNumber > 1);
      this.pagingButtons.isPrevious = (this.pagingDetails.pageNumber < this.pagingDetails.totalpages);
      this.pagingButtons.isLast = (this.pagingDetails.pageNumber < this.pagingDetails.totalpages);
      this.pagingButtons.isFirst = (this.pagingDetails.pageNumber > 1);
    } else if (workflowLength < this.limit && workflowLength !== 0) {
      // If the fetched rule is less than the limit
      this.pagingButtons.isNext = false;
      this.pagingButtons.isPrevious = (this.pagingDetails.pageNumber > 1);
      this.pagingButtons.isLast = (this.pagingDetails.pageNumber < this.pagingDetails.totalpages);
      this.pagingButtons.isFirst = (this.pagingDetails.pageNumber > 1);
      this.pagingDetails.start = ((this.pagingDetails.pageNumber - 1) * this.pagingDetails.pageSize);
      this.pagingDetails.end = workflowLength;
    }
  }

  /** 
   * Filter rules by type
   * @param type - 0 = All Rules, 1 = Visibility, 2 = Read-only, 3 = Mandatory
   * @param isReset - Whether to reset search when filtering
   */
  filterRulesByType(type: any, isReset?: boolean) {
    type = (isNaN(type)) ? 0 : parseInt(type);
    const typeText = this.getTypeText(type);

    if (isReset) {
      this.textToSearch = "";
      this.searchText = "";
      this.filteredData = [];
      this.pagingDetails.pageNumber = 1;
      this.pagingDetails.start = 1;
    }

    if (type === 0) {
      // All Rules - handle search if exists
      if (this.t1 === 1 || this.oldType !== type) {
        // If we apply filter, put back to page 1 when applying filter all
        this.t1 = 0;
        this.oldType = type;
        this.pagingDetails.pageNumber = 1;
        this.pagingDetails.start = 1;
      }

      // Set filterDataFieldRules based on whether search exists
      if (this.textToSearch && this.textToSearch !== "") {
        this.filterDataFieldRules = Helpers.deepClone(this.filteredData); // filterDataFieldRules contains all filtered rules
      } else {
        this.filterDataFieldRules = Helpers.deepClone(this.dataFieldRulesBackup); // filterDataFieldRules contains all rules
      }

      this.rulesCount = this.filterDataFieldRules ? this.filterDataFieldRules.length : 0;
      this.filterOrAll = 0;
      // Let getRulePageWises handle the pagination
      this.getRulePageWises();
    } else {
      // Filter by type (Visibility, Read-only, or Mandatory)
      // First filter by type from backup
      this.filterDataFieldRules = this.dataFieldRulesBackup.filter((rule: Rule) => {
        return _.get(rule, "type") === typeText;
      });

      if (this.t1 === 0 || this.oldType !== type) {
        // If we apply filter, put back to page 1
        this.t1 = 1;
        this.oldType = type;
        this.pagingDetails.pageNumber = 1;
        this.pagingDetails.start = 1;
      }

      this.rulesCount = this.filterDataFieldRules ? this.filterDataFieldRules.length : 0;
      this.filterOrAll = 1;
      // For filtered types, call getRulefilterWises which will handle pagination
      this.getRulefilterWises();
    }
  }

  prepareDataFieldRulesModal() {
    this.mapForRuletype();
    if (this.brandDetails.field_definitions) {
      this.dataFieldHash = JSON.parse(JSON.stringify(Object.assign(this.staticDataHash)));
      _.each(this.brandDetails.field_definitions, (field: any) => {
        // Filter out all the fields with custom_parameter_type as 4(The Custom Parameters)
        if (_.get(field, 'sequence_no') && _.get(field, "custom_parameter_type") == 4) {
          if (this.maxSequnceNo < field['sequence_no']) {
            this.maxSequnceNo = field['sequence_no']
          }
        }

        if ((_.get(field, "custom_parameter_type") == 4) && _.get(field, "hide_from_list") != true) {
          // && (Utils.getProp(field, "visible") == true)
          if ((_.get(field, "field_id") != 'ltms_custom_field_group_notes') && (_.get(field, "field_id") != 'ltms_custom_field_group_notes_rca')) {
            if (field.group_id) {
              if (field["is_deleted"] != true) {
                // field with group_id(The children)
                if (!this.dataFieldHash[field.group_id]) {
                  // If hash map doesn't contains parent entry.
                  this.dataFieldHash[field.group_id] = { "children": [] };
                }

                if (!this.dataFieldHash[field.group_id]["children"]) {
                  // If hash map contains parent entry but doesn't contains child field.
                  this.dataFieldHash[field.group_id]["children"] = [];
                }
                this.dataFieldHash[field.group_id]["children"].push(field);
              }
            } else {
              // field without group_id(The parent)
              this.dataFieldHash[field.field_id] = field;
            }
          } else {
            this.dataFieldHash[field.field_id] = field;
          }
        }
      });
      // Ignore the dataField with field "is_deleted" as true.
      var key = null;
      for (let key in this.dataFieldHash) {
        if (this.dataFieldHash[key]["is_deleted"] == true) {
          delete this.dataFieldHash[key];
        }
      }
      this.dataFieldBackup = Helpers.deepClone(this.dataFieldHash);
      var dataArr: any[] = this.sequentialSort(this.dataFieldHash, 'sequence_no')
      var arr: string[] = [];
      for (let item in dataArr) {
        arr.push(dataArr[item]['field_id'])
      }
      this.dataFieldHash = _.zipObject(arr, dataArr);
    }
  }

  /**
   * Prepare hierarchy mapping for organization levels
   */
  prepareHierarchyMapping() {
    if (!this.brandDetails || !this.actionService || _.isEmpty(this.brandDetails)) {
      console.warn('Cannot prepare hierarchy mapping: brandDetails or actionService not available');
      return;
    }

    try {
      // Check if brandDetails has required hierarchy data

      if (!this.brandDetails.hierarchy_list || _.isEmpty(this.brandDetails.hierarchy_list)) {
        console.warn('brandDetails.hierarchy_list is missing or empty');
        return;
      }

      const hierarchyList = this.actionService.prepareViewTypeDropdown(this.brandDetails);

      // Check if hierarchyList exists and has hierarchyMapping
      if (!hierarchyList) {
        console.warn('hierarchyList is null or undefined');
        return;
      }

      let unformattedMapping = hierarchyList.hierarchyMapping;
      let hierarchyAliasName = '';
      // Check if hierarchyMapping is empty or not an object
      if (!unformattedMapping || (Array.isArray(unformattedMapping) && unformattedMapping.length === 0) || (typeof unformattedMapping === 'object' && Object.keys(unformattedMapping).length === 0)) {
        console.warn('hierarchyMapping is empty. Checking brandDetails for hierarchy data...');

        // Try to build hierarchy mapping manually from brandDetails
        const hierarchyListFromBrand = this.brandDetails.hierarchy_list;
        const hierarchyListAliasValueFromBrand = this.brandDetails.hierarchy_list_alias;

        if (hierarchyListFromBrand && hierarchyListAliasValueFromBrand) {
          const hierarchyNames = Object.keys(hierarchyListFromBrand);
          let selectedHierarchyKey = hierarchyNames[hierarchyNames.length - 1];

          const hierarchyList = hierarchyListFromBrand[selectedHierarchyKey];
          const hierarchyListAliasValue = hierarchyListAliasValueFromBrand[selectedHierarchyKey]?.values;
          hierarchyAliasName = hierarchyListAliasValueFromBrand[selectedHierarchyKey]?.name;
          if (hierarchyList && hierarchyList.length > 0 && hierarchyListAliasValue && hierarchyListAliasValue.length > 0) {
            unformattedMapping = {};
            hierarchyListAliasValue.forEach((item: string, index: number) => {
              unformattedMapping[item] = hierarchyList[index] || 'Touchpoint';
            });
          }
        }

        if (!unformattedMapping || Object.keys(unformattedMapping).length === 0) {
          console.warn('No hierarchy mapping found after manual build attempt');
          return;
        }
      }

      // Get all keys from the object
      const keys = Object.keys(unformattedMapping);

      if (keys.length === 0) {
        console.warn('No hierarchy keys found in unformattedMapping');
        return;
      }

      // Ensure "assignees" key exists for "Only Assignee(s)" (matching old dashboard logic)
      if (!unformattedMapping['assignees']) {
        unformattedMapping['assignees'] = 'Only Assignee(s)';
      }

      // Ensure "Touchpoint" exists (check if any key has "Touchpoint" as value, if not add it)
      const hasTouchpoint = Object.values(unformattedMapping).some((val: any) => val === 'Touchpoint');
      if (!hasTouchpoint) {
        // Try to find h_0_5 or add a new key for Touchpoint
        const lastLevelKey = hierarchyAliasName + '_' + [keys.length - 1];
        if (!unformattedMapping[lastLevelKey]) {
          // Find the last hierarchy key pattern and add Touchpoint
          const hierarchyKeys = keys.filter(k => k.startsWith(lastLevelKey));
          if (hierarchyKeys.length > 0) {
            const lastHierarchyKey = hierarchyKeys[hierarchyKeys.length - 1];
            const keyParts = lastHierarchyKey.split('_');
            if (keyParts.length >= 3) {
              const nextIndex = parseInt(keyParts[2]) + 1;
              unformattedMapping[`${hierarchyAliasName}_${nextIndex}`] = 'Touchpoint';
            }
          } else {
            unformattedMapping[lastLevelKey] = 'Touchpoint';
          }
        }
      }

      this.hierarchyMapping = unformattedMapping;
      this.prepareTableRowsForDataGroupModal();
    } catch (error) {
      console.error('Error preparing hierarchy mapping:', error);
    }
  }

  mandatoryRuleType: any[] = [];
  visibleAndReadRuleType: any[] = [];
  visibleAndReadOptionValues: any[] = [];
  mandatoryRuleOptionValues: any[] = [];
  staticDataHash: any = {};
  staticDataFields: any[] = [];
  staticFieldIds: string[] = [];
  maxSequnceNo: number = 0;
  maxStaticSequenceNo: number = 0;
  getDataFields(fieldDefinitions: any) {
    return new Promise((resolve, reject) => {
      this.mandatoryRuleType = [];
      this.visibleAndReadRuleType = [];
      this.visibleAndReadOptionValues = [];
      this.staticDataHash = {};
      this._settingsService.getFieldDefinitions({ brand_id: this.brandId, type: 2 }).subscribe((response: any) => {
        if (response && response.data.code == 0 && response.data.accordions_list && response.data.accordions_list.length) {
          this.staticDataFields = Helpers.deepClone(response.data.accordions_list);
          _.each(response.data.accordions_list, (list: any) => {
            if (_.get(list, 'sequence_no') > 3) {
              this.staticFieldIds.push(list.field_id)
              this.staticDataHash[list.field_id] = list; // is-delete is-edit 
              list['name'] = list['display_name'];
              list['hide_delete'] = true; // to hide delete icon in table
              list['hide_newField'] = true;
              // if (list['field_id'] == 'status') {
              //     list['hide_editBtn'] = true; // to hide edit button icon in table
              // }
              this.maxSequnceNo = list['sequence_no'];
              if (this.maxSequnceNo < list['sequence_no']) {
                this.maxSequnceNo = list['sequence_no'];
              }
              this.maxStaticSequenceNo = this.maxSequnceNo;
              if (list['field_id'] != 'notes') {
                this.visibleAndReadRuleType.push({
                  "id": list['field_id'],
                  "text": list['display_name'],
                  "name": list['display_name']
                });
                this.visibleAndReadOptionValues.push({
                  "id": list['field_id'],
                  "name": list['display_name'],
                  "text": list['display_name']
                });
              }
            } else {
              // console.log('Sequence_no for static accordions not defined.')
            }
          })
        } else {
          this.staticDataHash = {};
        }
        resolve(true);
      }, (error: any) => {
        console.error('Error getting data fields:', error);
        resolve(false);
      });
    });
  }


  // Creating map for mandatory and visibility/editability dropdown in rule modal.
  // Visibility/read-only: static accordions + custom groups (with children for has_child).
  // Mandatory: custom groups only.
  mapForRuletype() {
    const customFieldDefinition = _.get(this.brandDetails, 'field_definitions') || [];
    const customParents: any[] = [];

    _.each(customFieldDefinition, (fields: any) => {
      if (fields['custom_parameter_type'] == 4 && _.get(fields, 'is_deleted') != true &&
        (_.get(fields, 'group_id') == undefined || _.get(fields, 'group_id') == null) &&
        fields['field_id'] != 'ltms_custom_field_group_notes' &&
        fields['field_id'] != 'ltms_custom_field_group_notes_rca') {

        const childOptions: any[] = [];
        _.each(customFieldDefinition, (child: any) => {
          if (child['custom_parameter_type'] == 4 &&
            _.get(child, 'is_deleted') != true &&
            _.get(child, 'group_id') == fields['field_id']) {
            childOptions.push({
              id: child['field_id'],
              name: child['name'] || child['display_name'],
              text: child['name'] || child['display_name']
            });
          }
        });

        // Also include children from dataFieldHash when available
        if ((!childOptions.length) && this.dataFieldHash?.[fields['field_id']]?.children?.length) {
          _.each(this.dataFieldHash[fields['field_id']].children, (child: any) => {
            if (child && child.is_deleted != true) {
              childOptions.push({
                id: child.field_id,
                name: child.name || child.display_name,
                text: child.name || child.display_name
              });
            }
          });
        }

        const option: any = {
          id: fields['field_id'],
          name: fields['name'] || fields['display_name'],
          text: fields['name'] || fields['display_name']
        };
        if (childOptions.length > 0) {
          option.children = childOptions;
        }
        customParents.push(option);
      }
    });

    // Rebuild custom entries on visibility list (keep static entries already added by getDataFields)
    this.visibleAndReadRuleType = (this.visibleAndReadRuleType || []).filter((opt: any) =>
      this.staticFieldIds.indexOf(opt.id) > -1
    );
    this.visibleAndReadOptionValues = (this.visibleAndReadOptionValues || []).filter((opt: any) =>
      this.staticFieldIds.indexOf(opt.id) > -1
    );
    // Ensure static options also have name for dropdown rendering
    this.visibleAndReadRuleType = this.visibleAndReadRuleType.map((opt: any) => ({
      ...opt,
      name: opt.name || opt.text,
      text: opt.text || opt.name
    }));
    this.visibleAndReadOptionValues = this.visibleAndReadOptionValues.map((opt: any) => ({
      ...opt,
      name: opt.name || opt.text,
      text: opt.text || opt.name
    }));

    this.mandatoryRuleType = [];
    this.mandatoryRuleOptionValues = [];

    _.each(customParents, (option: any) => {
      this.visibleAndReadRuleType.push(option);
      this.visibleAndReadOptionValues.push(option);
      this.mandatoryRuleType.push(option);
      this.mandatoryRuleOptionValues.push(option);
    });
  }

  /**
   * Prepare table rows for data group modal
   */
  prepareTableRowsForDataGroupModal() {
    let tableRows: any[] = [];

    if (!this.hierarchyMapping || _.isEmpty(this.hierarchyMapping)) {
      console.warn('No hierarchy mapping available for table rows');
      return;
    }

    const keys = Object.keys(this.hierarchyMapping);

    // Add all hierarchy levels (excluding assignees for now - will add at the end)
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      const label = this.hierarchyMapping[key];

      // Skip assignees here - will add it at the end
      if (label && key !== 'assignees') {
        tableRows.push({
          label,
          key,
          default: true, // mark as default
          visibility: true, // default visibility checked
          writeAccess: true // default write access checked
        });
      }
    }

    // Add "Only Assignee(s)" level as the LAST row (matching old dashboard logic)
    // Check if "assignees" key exists in hierarchy mapping
    if (this.hierarchyMapping['assignees']) {
      // Update existing assignees row to have proper label and position
      const assigneesIndex = tableRows.findIndex((row: any) => row.key === 'assignees');
      if (assigneesIndex !== -1) {
        // Remove from current position
        const assigneesRow = tableRows.splice(assigneesIndex, 1)[0];
        assigneesRow.label = 'Only Assignee(s)';
        assigneesRow.visibility = false; // assignees not visible by default
        assigneesRow.writeAccess = false; // assignees read-only by default
        // Add at the end
        tableRows.push(assigneesRow);
      } else {
        // Add new assignees row at the end
        tableRows.push({
          label: 'Only Assignee(s)',
          key: 'assignees',
          default: false, // assignees not checked by default for visibility
          visibility: false, // assignees not visible by default
          writeAccess: false // assignees read-only by default
        });
      }
    } else {
      // Add "Only Assignee(s)" at the end even if not in hierarchy mapping
      tableRows.push({
        label: 'Only Assignee(s)',
        key: 'assignees',
        default: false, // assignees not checked by default for visibility
        visibility: false, // assignees not visible by default
        writeAccess: false // assignees read-only by default
      });
    }

    this.tableRows = tableRows;

    // Update the modal schema with table rows
    // if (this.dataGroupModalSchema && this.dataGroupModalSchema.properties && this.dataGroupModalSchema.properties.organisationLevels) {
    //   this.dataGroupModalSchema.properties.organisationLevels.tableRows = tableRows;
    // }
  }

  /**
   * Reset all data to initial state
   */
  private resetData() {
    // Reset pagination variables
    this.rulesPageNo = 1;
    this.rulesPageFrom = 1;
    this.rulesPageTo = 1;
    this.rulesCount = 0;
    this.disableNext = true;
    this.disablePrev = true;
    this.isNextCtrl = true;

    // Reset search and filters
    this.textToSearch = '';
    this.searchText = '';
    this.selectedRuleTypeFilter = 0;
    this.filteredData = [];
    this.oldType = "";
    this.t1 = 1;
    this.indexFrom = 0;
    this.filterOrAll = 0;

    // Reset data arrays
    this.dataFieldHash = {};
    this.dataFieldBackup = {};
    this.dataFieldList = [];
    this.dataFieldListBackup = [];
    this.filteredFields = [];
    this.dataFieldRules = [];
    this.dataFieldRulesBackup = [];
    this.filterDataFieldRules = [];
  }

  /**
   * Main change handler - called when component initializes or data changes
   */
  private onChange() {
    this.pagerBusy = true;
    this.resetData();

    // Get brand ID from state or localStorage
    if (this.brandId) {
      // Get and prepare data field list data
      this.getDataFieldList();
    }
  }

  setViewMode(mode: 'basic' | 'advanced') {
    this.viewMode = mode;
    if (mode === 'advanced') {
      // Reset filters when switching to advanced view
      this.selectedRuleTypeFilter = 0;
      this.searchText = '';
      this.textToSearch = '';
      this.filteredData = [];
      this.rulesPageNo = 1;
      this.oldType = "";
      this.indexFrom = 0;
      // Apply default filter (all rules)
      setTimeout(() => {
        this.filterRulesByType(0, true);
      }, 0);
    }
  }

  /**
   * Get data field list from brand field_definitions
   */
  private getDataFieldList() {
    if (!this.brandDetails || !this.brandDetails.field_definitions) {
      this.pagerBusy = false;
      return;
    }

    try {
      const fieldDefinitions = this.brandDetails.field_definitions;
      let fieldDefsArray: any[] = [];

      // Handle both string and array formats
      if (typeof fieldDefinitions === 'string') {
        fieldDefsArray = JSON.parse(fieldDefinitions);
      } else if (Array.isArray(fieldDefinitions)) {
        fieldDefsArray = fieldDefinitions;
      }

      // Filter fields with custom_parameter_type == 4 and build hash structure
      this.dataFieldHash = {};
      const staticDataHash: any = {};

      // Process static accordions if available
      if (this.brandDetails.accordions_list && Array.isArray(this.brandDetails.accordions_list)) {
        this.brandDetails.accordions_list.forEach((list: any) => {
          if (list.sequence_no > 3) {
            staticDataHash[list.field_id] = {
              ...list,
              name: list.display_name,
              hide_delete: true,
              hide_newField: true
            };
          }
        });
      }

      // Start with static data
      this.dataFieldHash = JSON.parse(JSON.stringify(staticDataHash));

      // Process field definitions in a single pass (matching old dashboard logic exactly)
      fieldDefsArray.forEach((field: any) => {
        // Filter out all the fields with custom_parameter_type as 4 (matching old dashboard: no sequence_no requirement)
        if (field.custom_parameter_type == 4 && field.hide_from_list != true) {
          if (field.field_id != 'ltms_custom_field_group_notes' && field.field_id != 'ltms_custom_field_group_notes_rca') {
            if (field.group_id) {
              // Field with group_id (The children) - matching old dashboard logic exactly
              if (field.is_deleted != true) {
                // If hash map doesn't contain parent entry, create placeholder
                if (!this.dataFieldHash[field.group_id]) {
                  this.dataFieldHash[field.group_id] = { children: [] };
                }
                // If hash map contains parent entry but doesn't contain children array, create it
                if (!this.dataFieldHash[field.group_id].children) {
                  this.dataFieldHash[field.group_id].children = [];
                }
                // Add child to parent's children array
                this.dataFieldHash[field.group_id].children.push(field);
              }
            } else {
              // Field without group_id (The parent) - matching old dashboard logic
              // Only add if not deleted
              if (field.is_deleted != true) {
                // If parent already exists (from a child that was processed first), preserve children
                const existingChildren = this.dataFieldHash[field.field_id]?.children || [];
                this.dataFieldHash[field.field_id] = {
                  ...field,
                  children: existingChildren
                };
              } else {
                // If parent is deleted, remove it and all its children from hash
                if (this.dataFieldHash[field.field_id]) {
                  delete this.dataFieldHash[field.field_id];
                }
              }
            }
          } else {
            // Special fields - treat as parent
            const existingChildren = this.dataFieldHash[field.field_id]?.children || [];
            this.dataFieldHash[field.field_id] = {
              ...field,
              children: existingChildren
            };
          }
        }
      });

      // Remove deleted fields and their children
      Object.keys(this.dataFieldHash).forEach((key) => {
        if (this.dataFieldHash[key].is_deleted == true) {
          // Remove the deleted field and all its children
          delete this.dataFieldHash[key];
        }
      });

      // Also remove any children that belong to deleted parents
      Object.keys(this.dataFieldHash).forEach((key) => {
        if (this.dataFieldHash[key].children && this.dataFieldHash[key].children.length > 0) {
          // Filter out deleted children
          this.dataFieldHash[key].children = this.dataFieldHash[key].children.filter((child: any) => {
            return child.is_deleted != true;
          });
        }
      });

      // Sort children within each parent by seq_no (children use seq_no, parents use sequence_no)
      Object.keys(this.dataFieldHash).forEach((key) => {
        if (!this.dataFieldHash[key].children) {
          this.dataFieldHash[key].children = [];
        }
        if (this.dataFieldHash[key].children && this.dataFieldHash[key].children.length > 0) {
          // Sort children by seq_no, fallback to sequence_no if seq_no doesn't exist
          this.dataFieldHash[key].children.sort((a: any, b: any) => {
            const aSeq = a.seq_no !== undefined ? a.seq_no : (a.sequence_no !== undefined ? a.sequence_no : 0);
            const bSeq = b.seq_no !== undefined ? b.seq_no : (b.sequence_no !== undefined ? b.sequence_no : 0);
            return aSeq - bSeq;
          });
        }
      });

      // Sort by sequence_no and convert to array
      this.dataFieldBackup = _.cloneDeep(this.dataFieldHash);
      const sortedFields = this.sequentialSort(this.dataFieldHash, 'sequence_no');

      // Store grouped data for card-based display
      this.groupedDataFields = sortedFields;
      this.dataFieldList = sortedFields;
      this.dataFieldListBackup = _.cloneDeep(this.dataFieldList);

      // Also create flattened version for search
      const flattenedFields = this.flattenDataFields(sortedFields);
      this.filteredFields = flattenedFields;

      this.pagerBusy = false;
    } catch (error) {
      console.error('Error processing data fields:', error);
      this.pagerBusy = false;
    }
  }

  /**
   * Sort data by sequence
   */
  private sequentialSort(data: any, attr: string): any[] {
    const arr: any[] = [];
    Object.keys(data).forEach((prop) => {
      if (data.hasOwnProperty(prop)) {
        arr.push({
          key: prop,
          value: data[prop],
          tempSortName: data[prop][attr] || 0
        });
      }
    });

    arr.sort((a, b) => {
      const at = a.tempSortName || 0;
      const bt = b.tempSortName || 0;
      return at > bt ? 1 : (at < bt ? -1 : 0);
    });

    return arr.map((item) => item.value);
  }

  /**
   * Flatten data field structure for UI rendering with parent-child relationships
   */
  private flattenDataFields(fields: any[]): any[] {
    const list: any[] = [];
    fields.forEach((field) => {
      // Add parent field
      list.push({
        ...field,
        isParent: true,
        hasChildren: field.children && field.children.length > 0
      });
      // Add children if they exist
      if (field.children && field.children.length) {
        field.children.forEach((child: any, index: number) => {
          list.push({
            ...child,
            isChild: true,
            parentId: field.field_id,
            isLastChild: index === field.children.length - 1
          });
        });
      }
      // Add a marker for "New Field" button after each parent group
      // list.push({
      //   field_id: `new-field-${field.field_id}`,
      //   display_name: '',
      //   isNewFieldButton: !field?.hide_newField || false,
      //   parentId: field.field_id,
      //   parentField: field
      // });
    });
    return list;
  }

  /**
   * Get advanced data for rules
   */
  private getAdvancedData() {
    // This is now handled by prepareDataFieldRules which is called from the effect
    // Just ensure pagination is reset
    this.rulesPageNo = 1;
    this.selectedRuleTypeFilter = 0;
    this.textToSearch = '';
    this.searchText = '';
    this.filteredData = [];
    this.oldType = "";
    this.indexFrom = 0;
  }

  /**
   * Sort data by sequence
   */
  private sortBySequence(key: string, list: any[], params: any) {
    try {
      const sortedList = _.sortBy(list, (item: any) => {
        const value = params.is_date ? new Date(item[key]).getTime() : item[key];
        return params.asc ? -value : value;
      });
      const isToSortString = (!params.is_date && params.asc);
      return isToSortString ? sortedList.reverse() : sortedList;
    } catch (e) {
      return list;
    }
  }




  /**
   * Search functionality
   */
  onSearchChange() {
    if (this.viewMode === 'advanced') {
      this.searchRules(this.searchText);
    } else {
      this.searchDataFields(this.searchText, 'dataFieldList');
    }
  }

  /**
   * Search rules (for advanced view)
   * This matches the old dashboard searchByType logic
   */
  private searchRules(textToSearch: string) {
    this.textToSearch = "";
    this.filteredData = [];
    this.rulesPageNo = 1;
    this.indexFrom = 0;

    if (!textToSearch || textToSearch === "" || textToSearch === null || textToSearch === undefined) {
      // Apply condition to re-paginate after clearing search
      if (this.filterOrAll === 1) {
        this.getRulefilterWises();
      } else {
        this.getRulePageWises();
      }
    } else {
      // Convert textToSearch to lower case
      const searchText = textToSearch.toLowerCase();
      this.textToSearch = searchText;

      // Filter the list by the name
      this.filteredData = this.dataFieldRulesBackup.filter((rule: Rule) => {
        const ruleName = (rule.name || rule.description || '').toLowerCase();
        return ruleName.indexOf(searchText) >= 0;
      });

      // Apply current filter type (this will handle pagination)
      this.filterRulesByType(this.selectedRuleTypeFilter);
    }
  }

  /**
   * Get type text from filter number
   */
  private getTypeText(type: any): string {

    if (type === 1) return "visibility";
    if (type === 2) return "read-only";
    if (type === 3) return "mandatory";
    return "";
  }

  /**
   * Search data fields (for basic view)
   */
  private searchDataFields(textToSearch: string, settingType: string) {
    if (textToSearch && textToSearch.length) {
      const searchText = textToSearch.toLowerCase();

      // Filter grouped fields by search text
      this.groupedDataFields = this.dataFieldListBackup.filter((field: DataField) => {
        const fieldName = (field.display_name || field.name || '').toLowerCase();
        const matchesParent = fieldName.indexOf(searchText) >= 0;

        // Also check if any child matches
        if (field.children && field.children.length) {
          const childMatches = field.children.some((child: any) => {
            const childName = (child.display_name || child.name || '').toLowerCase();
            return childName.indexOf(searchText) >= 0;
          });
          return matchesParent || childMatches;
        }

        return matchesParent;
      });
    } else {
      this.groupedDataFields = [...this.dataFieldListBackup];
    }
  }

  /**
   * Close banner
   */
  closeBanner() {
    // Hide banner logic if needed
  }

  /**
   * Add new group or child field (opens modal)
   */
  addNewField(parentFieldId?: string) {
    if(this.readonly){
      Helpers.smallAlertBox('Access denied',"You do not have write permission","info",1500)
      return
    } 
    if (this.viewMode === 'basic') {
      if (parentFieldId) {
        // Adding child field to a parent group
        this.openChildFieldModal(parentFieldId);
      } else {
        // Adding new group
        this.openDataGroupModal();
      }
    } else if (this.viewMode === 'advanced') {
      this.openAdvancedRuleModal();
    }
  }

  /** Disable drag while searching, saving, or in readonly mode */
  isDragDisabled(): boolean {
    return this.readonly || !!(this.searchText && this.searchText.trim()) || this.pagerBusy;
  }

  trackByFieldId(_index: number, field: DataField): string {
    return field?.field_id || String(_index);
  }

  /**
   * Reorder parent groups (matching old rearrageRows for parents)
   * sequence_no = index + 4 (0-3 reserved for parameter/chat/profile/transaction)
   */
  onGroupDrop(event: CdkDragDrop<DataField[]>) {
    if(this.readonly){
      Helpers.smallAlertBox('Access denied',"You do not have write permission","info",1500)
      return
    }
    if (event.previousIndex === event.currentIndex || this.isDragDisabled()) {
      return;
    }
    moveItemInArray(this.groupedDataFields, event.previousIndex, event.currentIndex);
    this.groupedDataFields.forEach((group: any, index: number) => {
      group.sequence_no = index + 4;
      if (group.field_id && this.dataFieldHash[group.field_id]) {
        this.dataFieldHash[group.field_id].sequence_no = index + 4;
      }
    });
    this.dataFieldList = [...this.groupedDataFields];
    this.dataFieldListBackup = _.cloneDeep(this.groupedDataFields);
    this.rearrangeDataFields(this.dataFieldHash, false);
  }

  /**
   * Reorder child fields within a group (matching old updateSubItemsSequence)
   */
  onChildDrop(event: CdkDragDrop<DataField[] | undefined>, parentField: DataField) {
    if(this.readonly){
      Helpers.smallAlertBox('Access denied',"You do not have write permission","info",1500)
      return
    }
    if (event.previousIndex === event.currentIndex || this.isDragDisabled() || !parentField) {
      return;
    }
    if (!parentField.children) {
      parentField.children = [];
    }
    moveItemInArray(parentField.children, event.previousIndex, event.currentIndex);
    parentField.children.forEach((child: any, index: number) => {
      child.seq_no = index;
      child.sequence_no = index;
    });
    if (parentField.field_id && this.dataFieldHash[parentField.field_id]) {
      this.dataFieldHash[parentField.field_id].children = parentField.children;
    }
    this.dataFieldList = [...this.groupedDataFields];
    this.dataFieldListBackup = _.cloneDeep(this.groupedDataFields);
    this.rearrangeDataFields(this.dataFieldHash, true);
  }

  /**
   * Persist sequence after drag-drop (matching old rearrangeDataFields)
   */
  rearrangeDataFields(list: any, isSubItems?: boolean) {
    if (_.isEmpty(list)) {
      return;
    }

    const lists = Helpers.deepClone(list);
    const dataHashAfterSorting: any[] = [];

    _.each(lists, (value: any) => {
      if (_.get(value, 'children')) {
        _.each(value.children, (childValue: any) => {
          dataHashAfterSorting.push(childValue);
        });
        delete value.children;
        delete value.$$hashKey;
        dataHashAfterSorting.push(value);
      } else {
        delete value.$$hashKey;
        dataHashAfterSorting.push(value);
      }
    });

    const groups = _.groupBy(dataHashAfterSorting, 'custom_parameter_type');
    const customFields = Helpers.deepClone(groups['4'] || []);
    const staticAccordions = Helpers.deepClone(
      this.staticReservedAccordions.concat(groups['undefined'] || [])
    );

    this.pagerBusy = true;
    this._settingsService.updateFieldDefinition(this.brandId, customFields, null, null)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          const ok = response && (!response.error_message || response.error_message === 'Data already added.' || response.code === 0);
          if (ok) {
            this._settingsService.updateAccodionsList(this.brandId, staticAccordions, null, 1, null)
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe({
                next: () => {
                  Helpers.smallAlertBox('Success', 'Data fields sequence updated successfully.', 'Success');
                  this.afterUpdateBrandConfig();
                },
                error: (err: any) => {
                  this.pagerBusy = false;
                  console.error('Error updating accordion sequence:', err);
                  Helpers.smallAlertBox('Error', 'Problem while updating sequence.', 'error');
                }
              });
          } else {
            this.pagerBusy = false;
            Helpers.smallAlertBox('Error', 'Problem while updating sequence.', 'error');
          }
        },
        error: (err: any) => {
          this.pagerBusy = false;
          console.error('Error updating field sequence:', err);
          Helpers.smallAlertBox('Error', 'Problem while updating sequence.', 'error');
        }
      });
  }

  isAdvancedRuleModalOpen = false;

  /**
   * Open advanced rule modal for adding new rule
   */
  openAdvancedRuleModal() {
    this.ruleEditMode = false;
    this.selectedRuleDetails = {};
    this.ruleToReplace = null;
    this.ruleToEditId = null;
    this.dataRuleType = '';
    this.customType = null;
    this.lwDataFieldWorkflowActionsList = 0;

    this.mapForRuletype();
    this.populateFieldDefsMap();

    this.ruleModalSchema = _.cloneDeep(dataFieldRuleSchema);
    this.ruleModalSchema.title = 'Add Rule';
    if (this.ltmsEventList && this.ltmsEventList.length > 0) {
      this.ruleModalSchema.basic.properties.event_name.options = this.ltmsEventList;
    }

    this.ruleModalSchema.basic.properties.type.disabled = false;
    this.applyRuleTypeOptionsMap();
    this.updateRuleSetExcludeByType('');

    this.getWorkflowRules().then(() => {
      this.initializeRuleModal();
    });
  }

  /** Wire applies_to optionsMap for type-driven dropdown (matching old select2 swap) */
  private applyRuleTypeOptionsMap() {
    if (!this.ruleModalSchema?.basic?.properties?.applies_to) {
      return;
    }
    this.ruleModalSchema.basic.properties.applies_to.optionsMap = {
      mandatory: this.mandatoryRuleType,
      visibility: this.visibleAndReadRuleType,
      'read-only': this.visibleAndReadRuleType
    };
    // Seed options from current type when editing; leave empty for create until type selected
    if (this.dataRuleType && this.ruleModalSchema.basic.properties.applies_to.optionsMap[this.dataRuleType]) {
      this.ruleModalSchema.basic.properties.applies_to.options =
        this.ruleModalSchema.basic.properties.applies_to.optionsMap[this.dataRuleType];
    } else {
      this.ruleModalSchema.basic.properties.applies_to.options = [];
    }
  }

  /** Condition ruleset excludes flip with rule type (matching old workflow-data-field-condition) */
  private updateRuleSetExcludeByType(ruleType: string) {
    if (ruleType === 'mandatory') {
      this.ruleSetIdsToExclude = [
        'lw_event_fields_logic_ruleset',
        'do_api_call_to_check_for_cust_id',
        'lw_manager_fields_logic_ruleset',
        'response_fields_logic_ruleset'
      ];
    } else {
      this.ruleSetIdsToExclude = [
        'lw_event_fields_logic_ruleset',
        'do_api_call_to_check_for_cust_id',
        'response_fields_logic_mandatory_ruleset'
      ];
    }
  }

  /** Populate fieldDefsMap from brand field_definitions for conditions */
  private populateFieldDefsMap() {
    this.fieldDefsMap = {};
    const defs = _.get(this.brandDetails, 'field_definitions') || [];
    _.each(defs, (field: any) => {
      if (field && field.field_id && field.is_deleted != true) {
        this.fieldDefsMap[field.field_id] = field;
      }
    });
  }

  /**
   * Initialize rule modal with default values
   */
  private initializeRuleModal() {
    const defaultObject = _.cloneDeep(dataFieldRuleSchema.conditions.properties.dataObject);

    this.ruleDefaultValues = {
      uuid: '',
      created_date: '',
      is_enabled: '',
      name: '',
      type: '',
      event_name: '',
      applies_to: '',
      applies_to_child_field_ids: [],
      description: '',
      ignore_for_ui_rule: false,
      conditions: [defaultObject]
    };

    // Update schema with options
    this.updateRuleModalSchema();

    this.isRuleModalOpen = true;
    this.isAdvancedRuleModalOpen = true;
  }

  /**
   * Update rule modal schema with dynamic options
   */
  updateRuleModalSchema() {
    if (!this.ruleModalSchema.basic) {
      this.ruleModalSchema.basic = { properties: {} };
    }

    // Update event options
    if (this.ruleModalSchema.basic.properties.event_name) {
      this.ruleModalSchema.basic.properties.event_name.options = this.ltmsEventList;
    }

    // Keep optionsMap-driven applies_to options (do not overwrite with flat list)
    this.applyRuleTypeOptionsMap();

    // Update applies to child field options
    if (this.ruleModalSchema.basic.properties.applies_to_child_field_ids) {
      this.ruleModalSchema.basic.properties.applies_to_child_field_ids.options = this.appliesToChildFieldOptions;
    }

    // Update conditions properties
    if (!this.ruleModalSchema.conditions) {
      this.ruleModalSchema.conditions = { properties: {} };
    }
    this.ruleModalSchema.conditions.properties.rulesetlist = [...this.workflowFields];
    this.ruleModalSchema.conditions.properties.rulesetHash = { ...this.rulesetHash };
    this.ruleModalSchema.conditions.properties.fieldDefsMap = { ...this.fieldDefsMap };
  }

  /**
   * Prepare applies to options from data fields
   * @deprecated Prefer optionsMap via mapForRuletype / applyRuleTypeOptionsMap
   */
  prepareAppliesToOptions() {
    this.mapForRuletype();
    this.appliesToOptions = [...this.visibleAndReadRuleType];
  }

  /**
   * Get applies to child field options based on selected group
   * Matching old getAppliesToChildOptions (+ Select All)
   */
  getAppliesToChildOptions(groupId: string): any[] {
    const options: any[] = [];

    if (!groupId) {
      return options;
    }

    const customFieldDefinition = _.get(this.brandDetails, 'field_definitions') || [];
    _.each(customFieldDefinition, (fields: any) => {
      if (fields['custom_parameter_type'] == 4 &&
        _.get(fields, 'is_deleted') != true &&
        _.get(fields, 'group_id') == groupId) {
        options.push({
          id: fields['field_id'],
          name: fields['name'] || fields['display_name'],
          text: fields['name'] || fields['display_name']
        });
      }
    });

    // Fallback to dataFieldHash children
    if (!options.length && this.dataFieldHash?.[groupId]?.children?.length) {
      _.each(this.dataFieldHash[groupId].children, (child: any) => {
        if (child && child.is_deleted != true) {
          options.push({
            id: child.field_id,
            name: child.name || child.display_name,
            text: child.name || child.display_name
          });
        }
      });
    }

    if (options.length > 0) {
      options.unshift({
        id: 'all',
        name: 'Select All',
        text: 'Select All'
      });
    }

    return options;
  }

  /**
   * Get workflow rules for conditions
   */
  getWorkflowRules(): Promise<void> {
    return new Promise((resolve) => {
      this._settingsService.getWorkflowRules(this.brandId, 3)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (response: any) => {
            this.workflowFields = [];
            this.workflowOperators = [];
            this.rulesetHash = {};
            this.populateFieldDefsMap();

            let rulesets: any[] = [];
            if (response && response.data) {
              if (Array.isArray(response.data)) {
                rulesets = response.data;
              } else if (response.data.data && Array.isArray(response.data.data)) {
                rulesets = response.data.data;
              } else if (response.data.rulesets && Array.isArray(response.data.rulesets)) {
                rulesets = response.data.rulesets;
              }
            }

            // Filter out excluded rulesets (type-dependent)
            if (this.ruleSetIdsToExclude && this.ruleSetIdsToExclude.length) {
              rulesets = _.filter(rulesets, (ruleSet: any) => {
                return !(ruleSet._id && this.ruleSetIdsToExclude.indexOf(ruleSet._id) >= 0);
              });
            }

            // Process rulesets
            rulesets = _.map(rulesets, (condition: any) => {
              if (condition._id === 'response_fields_logic_ruleset') {
                condition = this.getExternalFields(condition);
              }
              condition.id = condition._id;
              condition.text = condition.name;
              this.rulesetHash[condition._id] = condition;
              return condition;
            });

            // Extract fields and operators
            _.each(rulesets, (rule: any) => {
              const options: any = _.get(rule, 'schema.options');
              const operators: any = _.get(rule, 'schema.operators');
              const rulesetId = rule._id;

              if (!_.isEmpty(options)) {
                _.each(options, (fieldDetail: any, key: string) => {
                  const valueType = _.get(fieldDetail, 'value_type');
                  if ((!_.hasIn(fieldDetail, 'operators') || _.isEmpty(fieldDetail.operators)) &&
                    !_.isEmpty(operators) && _.has(operators, valueType) &&
                    !_.isEmpty(operators[valueType])) {
                    fieldDetail.operators = _.cloneDeep(operators[valueType]);
                  }
                  fieldDetail.id = fieldDetail.unique_id;
                  fieldDetail.text = fieldDetail.display_name;
                  fieldDetail.ruleset_id = rulesetId;
                  fieldDetail.ruleset_key = key;
                  this.workflowFields.push(fieldDetail);
                });
              }
            });

            resolve();
          },
          error: (error: any) => {
            console.error('Error fetching workflow rules:', error);
            resolve();
          }
        });
    });
  }

  /**
   * Get external fields for response_fields_logic_ruleset
   */
  private getExternalFields(condition: any): any {
    // This method should handle external fields if needed
    // For now, return as is
    return condition;
  }

  /**
   * Get LTMS event list
   */
  getLtmsEventList(): Promise<void> {
    return new Promise((resolve) => {

      this._settingsService.getWorkflowRules(this.brandId, 1)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (response: any) => {
            this.ltmsEventList = [];

            let events: any[] = [];
            if (response && response.data) {
              if (Array.isArray(response.data)) {
                events = response.data;
              } else if (response.data.data && Array.isArray(response.data.data)) {
                events = response.data.data;
              }
            }

            // Filter events that apply to UIRule
            this.ltmsEventList = events.filter((event: any) => {
              return (event.applies_to && Array.isArray(event.applies_to) && event.applies_to.length > 0 && event.applies_to.indexOf('UIRule') > -1);
            });

            resolve();
          },
          error: (error: any) => {
            console.error('Error fetching events:', error);
            resolve();
          }
        });
    });
  }

  /**
   * Open data group modal for creating/editing
   */
  openDataGroupModal() {
    // Ensure hierarchy mapping is prepared before opening modal
    if (_.isEmpty(this.hierarchyMapping) || !this.tableRows || this.tableRows.length === 0) {
      this.prepareHierarchyMapping();
    }

    this.editMode = false;
    this.selectedGroupDetails = {};
    this.addGroupFlag = true;
    this.previousFieldName = '';
    this.openBooleanField = null;
    this.previousDisplayIn = null;
    this.previousUploadUserType = null;

    // Reset default values for new group
    // Structure: { [key]: { visibility: boolean, writeAccess: boolean } }
    const organisationLevels: any = {};
    if (this.tableRows && this.tableRows.length > 0) {
      this.tableRows.forEach((row: any) => {
        // For new group: all levels visible and writable except assignees
        const visibility = row.key === 'assignees' ? false : (row.visibility !== undefined ? row.visibility : true);
        const writeAccess = row.key === 'assignees' ? false : (row.writeAccess !== undefined ? row.writeAccess : true);

        organisationLevels[row.key] = {
          visibility: visibility,
          writeAccess: writeAccess
        };
      });
    }

    this.dataGroupModalDefaultValues = {
      name: '',
      always_open: false,
      display_in: 'default',
      user_upload_type: '',
      levels: []
    };

    // Reset modal schema for new group
    const modalSchemaCopy = _.cloneDeep(this.dataGroupModalSchema);
    modalSchemaCopy.title = 'Data Group';
    // Ensure tableRows are set in the schema

    this.dataGroupModalDefaultValues.levels = _.cloneDeep(this.tableRows);
    this.modalSchema = modalSchemaCopy;
    this.defaultValues = this.dataGroupModalDefaultValues;
    this.isModalOpen = true;
  }

  /**
   * Close data group modal
   */
  closeDataGroupModal() {
    this.isModalOpen = false;
    this.editMode = false;
    this.selectedGroupDetails = {};
    this.defaultValues = {
      group_name: '',
      always_open: false,
      display_in: 'default',
      toggle_read_write_access_for: '',
      set_level_based_visibility: {}
    };
  }

  /**
   * Edit field/group 
   */
  editField(field: DataField) {
    // Check if it's a parent group (no group_id) or a child field
    if (field.group_id) {
      // This is a child field - open child field edit modal
      this.openChildFieldModal(field.group_id, field);
      return;
    }

    // Ensure hierarchy mapping is prepared before opening modal
    if (_.isEmpty(this.hierarchyMapping) || !this.tableRows || this.tableRows.length === 0) {
      this.prepareHierarchyMapping();
    }

    // This is a parent group - open edit modal
    // Get the full field definition from field_definitions to ensure we have all properties
    let fullFieldData = _.cloneDeep(field);

    // Try to get the complete field definition from brandDetails.field_definitions
    if (this.brandDetails && this.brandDetails.field_definitions) {
      let fieldDefinitions: any[] = [];
      if (typeof this.brandDetails.field_definitions === 'string') {
        fieldDefinitions = JSON.parse(this.brandDetails.field_definitions);
      } else if (Array.isArray(this.brandDetails.field_definitions)) {
        fieldDefinitions = this.brandDetails.field_definitions;
      }

      // Find the matching field by field_id
      const matchingField = fieldDefinitions.find((f: any) => f.field_id === field.field_id);
      if (matchingField) {
        // Merge to get all properties including always_open, display_in, user_upload_type, etc.
        fullFieldData = {
          ...fullFieldData,
          ...matchingField
        };
      }
    }

    const clonedField = fullFieldData;
    this.selectedGroupDetails = clonedField;
    this.editMode = true;
    this.addGroupFlag = false;
    this.previousFieldName = clonedField.name || '';
    this.openBooleanField = clonedField.open;
    this.previousDisplayIn = clonedField.display_in;
    this.previousUploadUserType = clonedField.user_upload_type;

    // Determine level type - try both 'levels' and 'internal_levels' if user_upload_type is not set
    // Default to 'levels' if user_upload_type is not available
    const userUploadType = clonedField.user_upload_type || '';
    let levelType = userUploadType === 'manual_managed_users' ? 'internal_levels' : 'levels';

    // Get saved levels from field - handle both visibility and read-only (write access)
    // Important: Read from the PRIMARY level type based on user_upload_type
    // When user_upload_type == 'manual_managed_users': primary is 'internal_levels'
    // When user_upload_type == 'afp_users': primary is 'levels'
    const savedVisibilityLevels: string[] = [];
    const savedReadOnlyLevels: string[] = [];

    // Determine primary level type for reading (matching old dashboard logic)
    const primaryLevelTypeForRead = userUploadType === 'manual_managed_users' ? 'internal_levels' : 'levels';

    if (clonedField.access_key_rules && clonedField.access_key_rules.length > 0) {
      // Get visibility levels from PRIMARY level type
      const visibilityRule = clonedField.access_key_rules.find((rule: any) => rule.type === 'visibility');
      if (visibilityRule) {
        // Read from primary level type first
        if (visibilityRule[primaryLevelTypeForRead] && Array.isArray(visibilityRule[primaryLevelTypeForRead])) {
          savedVisibilityLevels.push(...visibilityRule[primaryLevelTypeForRead]);
        } else if (visibilityRule['levels'] && Array.isArray(visibilityRule['levels'])) {
          // Fallback to 'levels' if primary not found
          savedVisibilityLevels.push(...visibilityRule['levels']);
        } else if (visibilityRule['internal_levels'] && Array.isArray(visibilityRule['internal_levels'])) {
          // Fallback to 'internal_levels' if primary not found
          savedVisibilityLevels.push(...visibilityRule['internal_levels']);
        }
      }

      // Get read-only levels from PRIMARY level type (these are NOT writable)
      const readOnlyRule = clonedField.access_key_rules.find((rule: any) => rule.type === 'read-only');
      if (readOnlyRule) {
        // Read from primary level type first
        if (readOnlyRule[primaryLevelTypeForRead] && Array.isArray(readOnlyRule[primaryLevelTypeForRead])) {
          savedReadOnlyLevels.push(...readOnlyRule[primaryLevelTypeForRead]);
        } else if (readOnlyRule['levels'] && Array.isArray(readOnlyRule['levels'])) {
          // Fallback to 'levels' if primary not found
          savedReadOnlyLevels.push(...readOnlyRule['levels']);
        } else if (readOnlyRule['internal_levels'] && Array.isArray(readOnlyRule['internal_levels'])) {
          // Fallback to 'internal_levels' if primary not found
          savedReadOnlyLevels.push(...readOnlyRule['internal_levels']);
        }
      }
    }

    // Map saved visibility and write access levels to form format
    // Structure: { [key]: { visibility: boolean, writeAccess: boolean } }
    const organisationLevels: any = {};

    // Initialize all available levels
    // Matching old dashboard logic: writeAccessDetail = _.difference(latestHierarchy['values'], readOnlyAccess)
    // If readOnlyAccess is empty, writeAccessDetail = all hierarchy levels (all checked)
    // If readOnlyAccess has levels, writeAccessDetail = all hierarchy levels MINUS read-only levels
    if (this.tableRows && this.tableRows.length > 0) {
      // Get all hierarchy level keys
      const allHierarchyKeys = this.tableRows.map((row: any) => row.key);

      // Calculate write access: all hierarchy levels MINUS read-only levels
      // If read-only is empty, all hierarchy levels have write access (all checked)
      const allReadOnlyLevels = savedReadOnlyLevels.length > 0 ? [...savedReadOnlyLevels] : [];
      const writeAccessLevels = allReadOnlyLevels.length === 0
        ? allHierarchyKeys  // All levels have write access if read-only is empty
        : allHierarchyKeys.filter(key => !allReadOnlyLevels.includes(key)); // All levels MINUS read-only

      this.tableRows.forEach((row: any) => {
        const isVisible = savedVisibilityLevels.includes(row.key);
        // Write access: checked if level is in writeAccessLevels (matching old dashboard)
        // In old dashboard, ALL hierarchy levels are checked for write access by default when editing
        const hasWriteAccess = writeAccessLevels.includes(row.key);

        organisationLevels[row.key] = {
          visibility: isVisible,
          writeAccess: hasWriteAccess
        };
      });
    }

    // Update default values with field data - ensure all fields are populated
    // Note: Keys must match what toKey() generates from labels:
    // "Group Name" -> "group_name"
    // "Always Open" -> "always_open"
    // "Toggle Read/Write Access For" -> "toggle_read_write_access_for"
    // "Display in" -> "display_in"
    // "Set Level Based Visibility" -> "set_level_based_visibility"

    // Handle always_open - convert to boolean if needed (might be string "true"/"false" or number 1/0)
    // Check multiple possible property names and locations
    let alwaysOpenValue = false;
    const alwaysOpenSource = clonedField.always_open !== undefined ? clonedField.always_open :
      (clonedField.alwaysOpen !== undefined ? clonedField.alwaysOpen :
        (clonedField['always-open'] !== undefined ? clonedField['always-open'] : undefined));


    if (alwaysOpenSource !== undefined && alwaysOpenSource !== null) {
      if (typeof alwaysOpenSource === 'boolean') {
        alwaysOpenValue = alwaysOpenSource;
      } else if (typeof alwaysOpenSource === 'string') {
        alwaysOpenValue = alwaysOpenSource === 'true' || alwaysOpenSource === '1';
      } else if (typeof alwaysOpenSource === 'number') {
        alwaysOpenValue = alwaysOpenSource === 1;
      }
    } else {
      console.warn('Edit field - always_open property not found in field data. Defaulting to false.');
    }

    this.dataGroupModalDefaultValues = {
      name: clonedField.name || clonedField.display_name || '',
      always_open: alwaysOpenValue,
      display_in: clonedField.display_in || 'default',
      user_upload_type: userUploadType,
      levels: []
    };

    // Update modal title and schema for edit mode
    const modalSchemaCopy = _.cloneDeep(this.dataGroupModalSchema);
    modalSchemaCopy.title = 'Data Group';
    // Update tableRows in schema to use the updated defaults
    if (modalSchemaCopy.properties.levels) {
      const updatedTableRows = this.tableRows.map((row: any) => {
        const isVisible = savedVisibilityLevels.includes(row.key);
        const isReadOnly = savedReadOnlyLevels.includes(row.key);
        const hasWriteAccess = isVisible && !isReadOnly;

        return {
          ...row,
          default: (row.key === 'assignees') ? false : true, // Set default visibility
          visible: isVisible,
          writeAccess: hasWriteAccess
        };
      });
      this.dataGroupModalDefaultValues.levels = updatedTableRows;
    }

    // IMPORTANT: Set defaultValues FIRST, then schema, then open modal
    // This ensures the form initializes with the correct default values
    // Create a new object reference to trigger Angular change detection
    this.defaultValues = _.cloneDeep(this.dataGroupModalDefaultValues);


    // Set modal schema - this will trigger form initialization
    // The form should use the defaultValues we just set
    this.modalSchema = modalSchemaCopy;

    // Open modal
    this.isModalOpen = true;

    // Force update after form is fully initialized
    // Use a longer timeout to ensure the form component has processed everything
    setTimeout(() => {
      // Create a fresh reference to trigger change detection again
      // This ensures ngOnChanges is triggered in the form component
      const freshDefaults = _.cloneDeep(this.dataGroupModalDefaultValues);
      this.defaultValues = freshDefaults;
    }, 300);
  }

  /**
   * Handle form submission from modal (data group create/update)
   * Matching old dashboard submitForm for dataGroup
   */
  onFormSubmit(formData: any) {
    if(this.readonly){
      Helpers.smallAlertBox('Access denied',"You do not have write permission","info",1500)
      return
    }
    if (!formData) {
      return;
    }
    this.prepareReqParamsForDataGroup(formData);
  }

  /**
   * Prepare request parameters for data group save/update
   * Matching old: submitForm → insertDataField / updateDataField → insertFieldWithAccess → prepareReadWriteAccessLevel
   */
  prepareReqParamsForDataGroup(formData: any) {
    const dataField: any = {
      name: formData.name || '',
      open: formData.always_open === true,
      display_in: formData.display_in || 'default',
      user_upload_type: formData.user_upload_type || 'afp_users',
      custom_parameter_type: 4,
      group_id: null
    };

    if (this.editMode && this.selectedGroupDetails?.field_id) {
      dataField.field_id = this.selectedGroupDetails.field_id;
      dataField.sequence_no = this.selectedGroupDetails.sequence_no;
    }

    const levelsMap = this.convertLevelsTableToAccessMap(formData.levels);
    if (levelsMap) {
      dataField.levels = levelsMap;
    }

    const proceedSave = () => {
      this.checkForDuplicate('dataGroup', null, dataField, (hasDuplicateName: boolean) => {
        if (hasDuplicateName) {
          Helpers.smallAlertBox('Warning', 'Data Field name already exists, please enter another name.', 'warn');
          return;
        }
        if (this.editMode) {
          this.updateDataGroup(dataField);
        } else {
          dataField.sequence_no = ++this.maxSequnceNo;
          this.insertDataGroup(dataField);
        }
      });
    };

    if (this.editMode) {
      Helpers.showConfirmationBox(
        'Confirmation',
        'Please note that this change will Override the previous configuration. Are you sure, you want to proceed?',
        (answer: boolean) => {
          if (answer) {
            proceedSave();
          }
        }
      );
    } else {
      proceedSave();
    }
  }

  /**
   * Convert checkbox table rows / object into { visibility: [], 'write-only': [] }
   */
  private convertLevelsTableToAccessMap(levelsData: any): { visibility: string[]; 'write-only': string[] } | null {
    if (!levelsData) {
      return null;
    }

    const visibility: string[] = [];
    const writeOnly: string[] = [];

    if (Array.isArray(levelsData)) {
      levelsData.forEach((row: any) => {
        if (!row || !row.key) {
          return;
        }
        const isVisible = row.visibility === true || row.visible === true || row.value === true;
        const hasWriteAccess = row.writeAccess === true || row['write-access'] === true;
        if (isVisible) {
          visibility.push(row.key);
        }
        if (hasWriteAccess) {
          writeOnly.push(row.key);
        }
      });
    } else if (typeof levelsData === 'object') {
      Object.keys(levelsData).forEach((key: string) => {
        const value = levelsData[key];
        if (typeof value === 'object' && value !== null) {
          const isVisible = value.visibility === true || value.visible === true || value.value === true;
          const hasWriteAccess = value.writeAccess === true || value['write-access'] === true;
          if (isVisible) {
            visibility.push(key);
          }
          if (hasWriteAccess) {
            writeOnly.push(key);
          }
        } else if (value === true) {
          visibility.push(key);
          writeOnly.push(key);
        }
      });
    } else {
      return null;
    }

    return { visibility, 'write-only': writeOnly };
  }

  /**
   * Latest hierarchy level keys (including assignees) for access calculations
   */
  private getLatestHierarchyValues(): string[] {
    if (this.tableRows && this.tableRows.length > 0) {
      return this.tableRows.map((row: any) => row.key).filter(Boolean);
    }

    const hierarchyListAlias = _.cloneDeep(_.get(this.brandDetails, 'hierarchy_list_alias') || {});
    const hierarchyNames = Object.keys(hierarchyListAlias);
    if (!hierarchyNames.length) {
      return ['assignees'];
    }
    const latest = hierarchyListAlias[hierarchyNames[hierarchyNames.length - 1]] || {};
    const values = Array.isArray(latest.values) ? [...latest.values] : [];
    if (values.indexOf('assignees') === -1) {
      values.push('assignees');
    }
    return values;
  }

  /**
   * Matching old insertFieldWithAccess for groups
   */
  private insertFieldWithAccess(type: string, dataField: any): any {
    let isShowError = true;
    let levelType = 'levels';

    if (type === 'insert' || _.get(dataField, 'group_id') || type === 'deleteParent' || type === 'deleteChild') {
      isShowError = false;
    }

    if (_.get(dataField, 'user_upload_type') === 'manual_managed_users') {
      levelType = 'internal_levels';
    } else {
      levelType = 'levels';
    }

    // Form always sends "levels"; remap when manual_managed_users
    if (levelType === 'internal_levels' && dataField.levels && !dataField.internal_levels) {
      dataField.internal_levels = dataField.levels;
      delete dataField.levels;
    }

    const isModified =
      this.previousFieldName === _.get(dataField, 'name') &&
      this.openBooleanField === _.get(dataField, 'open') &&
      this.previousDisplayIn === _.get(dataField, 'display_in') &&
      this.previousUploadUserType === _.get(dataField, 'user_upload_type');

    if (!_.get(dataField, levelType) && isModified && isShowError) {
      return 'error';
    }

    const dataValues = dataField;
    let levels = dataValues[levelType] || {};
    const latestHierarchyValues = this.getLatestHierarchyValues();

    if (!this.addGroupFlag && !isModified && !_.get(dataField, levelType)) {
      const existingRules = _.get(this.selectedGroupDetails, 'access_key_rules');
      if (existingRules && existingRules.length) {
        dataValues[levelType] = {};
        const fields: any = {};
        _.each(existingRules, (accessRule: any) => {
          if (accessRule['type'] === 'visibility') {
            fields['visibility'] = accessRule[levelType];
          } else if (accessRule['type'] === 'read-only') {
            fields['write-only'] = _.difference(latestHierarchyValues, accessRule[levelType] || []);
          }
        });
        levels = Object.assign(fields);
        dataValues[levelType] = levels;
      }
    }

    let readOnlyValues: string[] = latestHierarchyValues;
    if (_.get(levels, 'write-only') || _.get(levels, 'visibility')) {
      if (_.get(levels, 'write-only')) {
        if (levels['write-only'].length) {
          readOnlyValues = _.difference(latestHierarchyValues, levels['write-only']);
        } else {
          readOnlyValues = latestHierarchyValues;
        }
      }
      if (_.get(levels, 'visibility') && levels['visibility'].length) {
        dataValues[levelType]['visibility'] = levels['visibility'];
      }
    }

    if (_.get(dataValues, levelType) && _.get(dataValues[levelType], 'write-only')) {
      delete dataValues[levelType]['write-only'];
      dataValues[levelType]['read-only'] = readOnlyValues;
    } else if (_.get(dataValues, levelType)) {
      dataValues[levelType]['read-only'] = readOnlyValues;
    }

    return dataValues;
  }

  /**
   * Matching old prepareReadWriteAccessLevel
   */
  private prepareReadWriteAccessLevel(dataField: any): any {
    const accessKeyRules: any[] = [];
    let visibilityLevelsToRetrive: string[] = [];
    let readOnlyLevelsToRetrive: string[] = [];
    let levelsTypeToRetrive: string | null = null;
    let levelsType = 'levels';

    if (_.get(dataField, 'user_upload_type') === 'manual_managed_users') {
      levelsTypeToRetrive = 'levels';
      levelsType = 'internal_levels';
    } else if (_.get(dataField, 'user_upload_type') === 'afp_users') {
      levelsTypeToRetrive = 'internal_levels';
      levelsType = 'levels';
    }

    const selectedField = this.dataFieldHash[_.get(dataField, 'field_id')] || this.selectedGroupDetails;
    const availableAccesses = this.getAccessLevelsForUserType(_.get(dataField, 'user_upload_type'));
    let levelValues: string[] = [];

    if (levelsTypeToRetrive && _.get(selectedField, 'access_key_rules')) {
      _.each(selectedField['access_key_rules'], (value: any) => {
        if (value[levelsTypeToRetrive as string] && value['type'] === 'visibility') {
          visibilityLevelsToRetrive = value[levelsTypeToRetrive as string];
        } else if (value[levelsTypeToRetrive as string] && value['type'] === 'read-only') {
          readOnlyLevelsToRetrive = value[levelsTypeToRetrive as string];
        }
      });
    } else if (levelsTypeToRetrive && availableAccesses.length) {
      levelValues = availableAccesses.filter((v: any) => v.visibility).map((v: any) => v.id);
    }

    if (!_.isEmpty(dataField) && dataField[levelsType]) {
      for (const key in dataField[levelsType]) {
        if (!Object.prototype.hasOwnProperty.call(dataField[levelsType], key)) {
          continue;
        }
        if (levelsType === 'internal_levels') {
          accessKeyRules.push({
            type: key,
            internal_levels: _.uniq(dataField[levelsType][key] || [])
          });
        } else {
          accessKeyRules.push({
            type: key,
            levels: _.uniq(dataField[levelsType][key] || [])
          });
        }
      }

      if (accessKeyRules.length && levelsTypeToRetrive) {
        _.each(accessKeyRules, (accessRule: any) => {
          if (accessRule.type === 'visibility') {
            accessRule[levelsTypeToRetrive as string] = visibilityLevelsToRetrive;
          } else if (accessRule.type === 'read-only') {
            accessRule[levelsTypeToRetrive as string] = readOnlyLevelsToRetrive;
          }
        });
      }

      dataField.access_key_rules = accessKeyRules;

      if (!_.get(selectedField, 'access_key_rules') && availableAccesses.length && levelsTypeToRetrive) {
        _.each(dataField.access_key_rules, (value: any) => {
          if (value.type === 'visibility') {
            value[levelsTypeToRetrive as string] = levelValues;
          } else if (value.type === 'read-only') {
            value[levelsTypeToRetrive as string] = ['assignees'];
          }
        });
      }

      delete dataField[levelsType];
      return dataField;
    }

    // Fallback defaults when levels map is missing
    const defaultLevels = this.getLatestHierarchyValues().filter((k: string) => k !== 'assignees');
    accessKeyRules.push(
      { type: 'visibility', levels: defaultLevels, internal_levels: defaultLevels },
      { type: 'read-only', levels: ['assignees'], internal_levels: ['assignees'] }
    );
    dataField.access_key_rules = accessKeyRules;
    return dataField;
  }

  private getAccessLevelsForUserType(userUploadType: string): any[] {
    const levelType = userUploadType === 'manual_managed_users' ? 'internal_levels' : 'levels';
    const values = this.getLatestHierarchyValues();
    return values.map((id: string) => ({
      id,
      visibility: id !== 'assignees',
      levelType
    }));
  }

  /**
   * Insert new data group
   */
  private insertDataGroup(dataField: any) {
    this.pagerBusy = true;
    let prepared = this.insertFieldWithAccess('insert', dataField);
    if (!prepared || prepared === 'error' || _.isEmpty(prepared)) {
      this.pagerBusy = false;
      Helpers.smallAlertBox('Error', 'Invalid Data Field.', 'error');
      return;
    }

    prepared = this.prepareReadWriteAccessLevel(prepared);
    delete prepared.children;

    this._settingsService.insertFieldDefinitions(this.brandDetails._id, [prepared])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          this.pagerBusy = false;
          if (response && response.code === 0) {
            Helpers.smallAlertBox('Success', 'Data Field added successfully.', 'Success');
            this.closeDataGroupModal();
            this.afterUpdateBrandConfig();
          } else if (response && response.code === 4021) {
            Helpers.smallAlertBox('Error', 'Data Field already exists.', 'error');
          } else {
            Helpers.smallAlertBox('Error', response?.error_message || 'Problem while adding data field.', 'error');
          }
        },
        error: (err: any) => {
          this.pagerBusy = false;
          console.error('Error inserting data group:', err);
          Helpers.smallAlertBox('Error', 'Problem while adding data field.', 'error');
        }
      });
  }

  /**
   * Update existing data group (merge with existing hash entry like old getSequenceNo)
   */
  private updateDataGroup(dataField: any) {
    this.pagerBusy = true;
    let prepared = this.insertFieldWithAccess('update', dataField);
    if (prepared === 'error') {
      this.pagerBusy = false;
      Helpers.smallAlertBox('Warning', 'No changes made.', 'warn');
      return;
    }
    if (!prepared || _.isEmpty(prepared)) {
      this.pagerBusy = false;
      Helpers.smallAlertBox('Error', 'Invalid Data Field.', 'error');
      return;
    }

    prepared = this.prepareReadWriteAccessLevel(prepared);

    const fieldTobeUpdated = this.dataFieldHash[dataField.field_id] || this.selectedGroupDetails;
    let dataFieldValue = prepared;

    if (fieldTobeUpdated && !prepared.group_id) {
      dataFieldValue = Helpers.deepClone(fieldTobeUpdated);
      delete dataFieldValue.children;

      const isAccessKeyRulesToDelete =
        (_.get(dataField, 'display_in') && dataField.display_in !== this.previousDisplayIn) ||
        (_.get(dataField, 'open') !== undefined && this.openBooleanField !== dataField.open) ||
        (this.previousFieldName !== dataField.name);

      if (_.get(prepared, 'access_key_rules') && !isAccessKeyRulesToDelete) {
        dataFieldValue.access_key_rules = prepared.access_key_rules;
      }

      if (dataField.hasOwnProperty('open')) {
        dataFieldValue.open = dataField.open;
      }
      if (_.get(dataField, 'name')) {
        dataFieldValue.name = dataField.name;
      }
      if (dataField.hasOwnProperty('display_in')) {
        dataFieldValue.display_in = dataField.display_in;
      }
      if (dataField.hasOwnProperty('user_upload_type')) {
        dataFieldValue.user_upload_type = dataField.user_upload_type;
        // When user type changes, always take newly prepared access rules
        if (dataField.user_upload_type !== this.previousUploadUserType && _.get(prepared, 'access_key_rules')) {
          dataFieldValue.access_key_rules = prepared.access_key_rules;
        }
      }
    }

    delete dataFieldValue.children;

    this._settingsService.updateFieldDefinition(this.brandDetails._id, [dataFieldValue], null, null)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          this.pagerBusy = false;
          if (response && !response.error_message && response.code === 0) {
            Helpers.smallAlertBox('Success', 'Data Field updated successfully.', 'Success');
            this.closeDataGroupModal();
            this.afterUpdateBrandConfig();
          } else if (response && response.code === 4021) {
            Helpers.smallAlertBox('Warning', 'No changes made.', 'warn');
          } else {
            Helpers.smallAlertBox('Error', response?.error_message || 'Problem while updating field.', 'error');
          }
        },
        error: (err: any) => {
          this.pagerBusy = false;
          console.error('Error updating data group:', err);
          Helpers.smallAlertBox('Error', 'Problem while updating field.', 'error');
        }
      });
  }

  /**
   * Open child field modal for creating/editing
   * Matching old editBasicDataField addChild / editChild → openModalBasedOnType
   */
  openChildFieldModal(parentFieldId: string, childField?: DataField) {
    this.currentParentFieldId = parentFieldId;
    this.childFieldEditMode = !!childField;

    const baseDefaults = _.cloneDeep(this.childFieldModalDefaultValues);

    if (childField) {
      this.selectedChildFieldDetails = _.cloneDeep(childField);

      if (this.brandDetails && this.brandDetails.field_definitions) {
        let fieldDefinitions: any[] = [];
        if (typeof this.brandDetails.field_definitions === 'string') {
          fieldDefinitions = JSON.parse(this.brandDetails.field_definitions);
        } else if (Array.isArray(this.brandDetails.field_definitions)) {
          fieldDefinitions = this.brandDetails.field_definitions;
        }

        const matchingField = fieldDefinitions.find((f: any) => f.field_id === childField.field_id);
        if (matchingField) {
          this.selectedChildFieldDetails = {
            ...this.selectedChildFieldDetails,
            ...matchingField
          };
        }
      }

      const details = this.selectedChildFieldDetails;
      const dom = details.dom_options || {};
      let fieldType = details.data_type || '';
      // When stored as group, restore group_to_render as field type for the dropdown
      if (fieldType === 'group' && dom.group_to_render) {
        fieldType = dom.group_to_render;
      }

      const listDs = details.datasource || {};
      const listOpts = listDs.options || {};
      let listDataSource = 'individual';
      let listIndividualValues = '';
      let listApiMethod = 'GET';
      let listApiUrl = '';
      let listApiValueField = '';
      let listApiDisplayField = '';

      if (fieldType === 'list' && listDs) {
        if (listDs.type === 2) {
          listDataSource = 'api';
          listApiMethod = listOpts.method || 'GET';
          listApiUrl = listOpts.url || '';
          listApiValueField = listOpts.value_field || '';
          listApiDisplayField = listOpts.display_field || '';
        } else if (listDs.type === 1) {
          listDataSource = 'mapping';
        } else {
          listDataSource = 'individual';
          const vals = listOpts.values;
          listIndividualValues = Array.isArray(vals) ? vals.join(',') : (vals || '');
        }
      }

      let linkUrls = '';
      if (fieldType === 'link' && details.datasource?.options?.values) {
        const vals = details.datasource.options.values;
        linkUrls = Array.isArray(vals) ? vals.join(',') : String(vals);
      }

      this.childFieldDefaultValues = {
        ...baseDefaults,
        field_name: details.name || '',
        display_name: details.display_name || '',
        data_category: details.data_category || '',
        field_type: fieldType,
        min_value: dom.min !== undefined && dom.min !== false ? String(dom.min) : '',
        max_value: (fieldType === 'number' && dom.max !== undefined && dom.max !== false) ? String(dom.max) : '',
        allow_multiline: dom.type === 'multi-line' || dom.type === true,
        textbox_height: dom.rows !== undefined ? String(dom.rows) : '',
        max_characters: (fieldType === 'string' && dom.max !== undefined && dom.max !== false) ? String(dom.max) : '',
        date_format: dom.format || '',
        link_urls: linkUrls,
        list_data_source: listDataSource,
        list_individual_values: listIndividualValues,
        list_dataset_id: listOpts.dataset_id || '',
        list_parent_field_id: details.parent_field_id || '',
        list_api_method: listApiMethod,
        list_api_url: listApiUrl,
        list_api_value_field: listApiValueField,
        list_api_display_field: listApiDisplayField,
        mandatory: dom.required !== undefined ? dom.required : true,
        allow_multiple: dom.allow_multi || false,
        read_only: dom.read_only || false,
        download_enabled: details.download || false,
        add_to_filters: details.es_filter || false,
        add_to_cohorts: details.compare_filter || false,
        inbox_search_enabled: details.inbox_search_enabled || false,
        show_field_label: dom.label || false,
        show_in_inbox_detail: details.show_in_inbox_detail !== undefined ? details.show_in_inbox_detail : true,
        restrict_backdating: dom.disable_back_date || false
      };
    } else {
      this.selectedChildFieldDetails = {};
      this.childFieldDefaultValues = baseDefaults;
    }

    const schemaCopy = _.cloneDeep(this.childFieldModalSchemaBase || this.childFieldModalSchema);
    if (!this.childFieldModalSchemaBase) {
      this.childFieldModalSchemaBase = _.cloneDeep(this.childFieldModalSchema);
    }
    this.updateFieldTypeOptions(schemaCopy);
    // Make date_format / link_urls required when those types are selected (evaluated on submit via visible fields)
    if (_.get(schemaCopy, 'basic.properties.date_format') && this.childFieldDefaultValues.field_type === 'date') {
      schemaCopy.basic.properties.date_format.required = true;
      schemaCopy.basic.properties.date_format.hidden = false;
    }
    if (_.get(schemaCopy, 'basic.properties.link_urls') && this.childFieldDefaultValues.field_type === 'link') {
      schemaCopy.basic.properties.link_urls.required = true;
      schemaCopy.basic.properties.link_urls.hidden = false;
    }

    this.childFieldModalSchema = schemaCopy;
    this.isChildFieldModalOpen = true;
  }

  /**
   * Update field type options to include other parent groups (matching old getModalOptions addChild)
   */
  private updateFieldTypeOptions(schema: any) {
    const fieldTypeProp = _.get(schema, 'basic.properties.field_type') || _.get(schema, 'properties.field_type');
    if (!fieldTypeProp) {
      return;
    }

    const inputOptions = [
      { id: 'attachment', name: 'Attachment' },
      { id: 'number', name: 'Number' },
      { id: 'string', name: 'String' },
      { id: 'date', name: 'Date' },
      { id: 'list', name: 'List' },
      { id: 'datetime', name: 'Date and Time' },
      { id: 'link', name: 'Link' }
    ];

    const groupOptions: any[] = [];
    if (this.brandDetails && this.brandDetails.field_definitions) {
      let fieldDefinitions: any[] = [];
      if (typeof this.brandDetails.field_definitions === 'string') {
        fieldDefinitions = JSON.parse(this.brandDetails.field_definitions);
      } else if (Array.isArray(this.brandDetails.field_definitions)) {
        fieldDefinitions = this.brandDetails.field_definitions;
      }

      fieldDefinitions.forEach((f: any) => {
        if (f.custom_parameter_type === 4 &&
          !f.group_id &&
          f.field_id !== this.currentParentFieldId &&
          !f.is_deleted) {
          // Old dashboard uses field_id as option id (often already ltms_custom_field_*)
          groupOptions.push({
            id: f.field_id,
            name: f.name || f.display_name
          });
        }
      });
    }

    fieldTypeProp.options = [...inputOptions, ...groupOptions];
    fieldTypeProp.textField = 'name';
    fieldTypeProp.valueField = 'id';

    // Parent field options for list Data Mapping (sibling list fields in same group)
    const parentFieldProp = _.get(schema, 'basic.properties.list_parent_field_id');
    if (parentFieldProp) {
      const parentOptions: any[] = [];
      const parentGroup = this.dataFieldHash[this.currentParentFieldId];
      if (parentGroup && parentGroup.children) {
        parentGroup.children.forEach((child: any) => {
          if (child.data_type === 'list' && _.get(child, 'datasource.type') === 1 &&
            child.field_id !== this.selectedChildFieldDetails?.field_id) {
            parentOptions.push({ id: child.field_id, name: child.name || child.display_name });
          }
        });
      }
      parentFieldProp.options = parentOptions;
    }
  }

  closeChildFieldModal() {
    this.isChildFieldModalOpen = false;
    this.childFieldEditMode = false;
    this.selectedChildFieldDetails = {};
    this.currentParentFieldId = '';
    this.childFieldDefaultValues = _.cloneDeep(this.childFieldModalDefaultValues);
  }

  /**
   * Handle child field form submission
   * Matching old submitForm for dataField (with confirm on update + duplicate check)
   */
  onChildFieldFormSubmit(formData: any) {
    if(this.readonly){
      Helpers.smallAlertBox('Access denied',"You do not have write permission","info",1500)
      return
    }
    if (!formData) {
      return;
    }

    const fieldName = formData.field_name || formData.name || '';
    const objectToCheck: any = {
      name: fieldName,
      field_id: this.childFieldEditMode ? this.selectedChildFieldDetails?.field_id : undefined
    };

    const proceedSave = () => {
      this.checkForDuplicate('dataField', this.currentParentFieldId, objectToCheck, (hasDuplicateName: boolean) => {
        if (hasDuplicateName) {
          Helpers.smallAlertBox('Warning', 'Data Field name already exists, please enter another name.', 'warn');
          return;
        }
        this.prepareReqParamsForChildField(formData);
      });
    };

    if (this.childFieldEditMode) {
      Helpers.showConfirmationBox(
        'Confirmation',
        'Please note that this change will Override the previous configuration. Are you sure, you want to proceed?',
        (answer: boolean) => {
          if (answer) {
            proceedSave();
          }
        }
      );
    } else {
      proceedSave();
    }
  }

  /**
   * Prepare request parameters for child field save/update
   * Matching old insertDataField / updateDataField for group_id children
   */
  prepareReqParamsForChildField(formData: any) {
    const fieldName = formData.field_name || formData.name || '';
    const displayName = formData.display_name || '';
    const dataCategory = formData.data_category || '';
    let fieldType = formData.field_type || '';

    if (!fieldName || !displayName || !dataCategory || !fieldType) {
      Helpers.smallAlertBox('Error', 'Please fill all the required fields', 'error');
      return;
    }

    // Type-specific validation (matching old list/link/date requirements)
    if (fieldType === 'date' && !formData.date_format) {
      Helpers.smallAlertBox('Error', 'Date Format is required.', 'error');
      return;
    }
    if (fieldType === 'link' && !formData.link_urls) {
      Helpers.smallAlertBox('Error', 'URL is required.', 'error');
      return;
    }
    if (fieldType === 'list') {
      const source = formData.list_data_source || 'individual';
      if (source === 'individual') {
        if (!formData.list_individual_values || !String(formData.list_individual_values).trim()) {
          Helpers.smallAlertBox('Error', 'Values is mandatory.', 'error');
          return;
        }
        const values = String(formData.list_individual_values).split(',').map((v: string) => v.trim()).filter(Boolean);
        if (_.uniq(values).length !== values.length) {
          Helpers.smallAlertBox('Error', 'Duplicate Values in the List.', 'error');
          return;
        }
      } else if (source === 'mapping') {
        if (!formData.list_dataset_id) {
          Helpers.smallAlertBox('Error', 'Data Set is mandatory.', 'error');
          return;
        }
      } else if (source === 'api') {
        if (!formData.list_api_url) {
          Helpers.smallAlertBox('Error', 'API Url is mandatory.', 'error');
          return;
        }
      }
    }

    const domOptions: any = {};

    if (formData.mandatory !== undefined) {
      domOptions.required = formData.mandatory;
    } else {
      domOptions.required = true;
    }

    if (formData.allow_multiple !== undefined) {
      domOptions.allow_multi = formData.allow_multiple;
    }

    if (formData.read_only !== undefined) {
      domOptions.read_only = formData.read_only;
    }

    if (formData.show_field_label !== undefined) {
      domOptions.label = formData.show_field_label;
    }

    // String type options (matching old getOptionsForString + insertDataField)
    if (fieldType === 'string') {
      if (formData.allow_multiline === true || formData.allow_multiline === 'true') {
        domOptions.type = 'multi-line';
      } else {
        domOptions.type = 'single-line';
      }
      if (formData.textbox_height !== undefined && formData.textbox_height !== '') {
        domOptions.rows = formData.textbox_height;
      }
      if (formData.max_characters !== undefined && formData.max_characters !== '') {
        domOptions.max = formData.max_characters;
      }
    }

    // Number type
    if (fieldType === 'number') {
      if (formData.min_value !== undefined && formData.min_value !== false && formData.min_value !== null && formData.min_value !== '') {
        domOptions.min = formData.min_value;
      }
      if (formData.max_value !== undefined && formData.max_value !== false && formData.max_value !== null && formData.max_value !== '') {
        domOptions.max = formData.max_value;
      }
      if (domOptions.min !== undefined && domOptions.max !== undefined) {
        if (parseInt(domOptions.min, 10) > parseInt(domOptions.max, 10)) {
          Helpers.smallAlertBox('Error', "Invalid digit count. Minimum value can't be greater than Maximum value.", 'error');
          return;
        }
      }
    }

    // Date / DateTime
    if (fieldType === 'date' || fieldType === 'datetime') {
      if (fieldType === 'date' && formData.date_format) {
        domOptions.format = formData.date_format;
      }
      if (formData.restrict_backdating !== undefined) {
        domOptions.disable_back_date = formData.restrict_backdating;
      }
    }

    // Attachment: force allow_multi false
    if (fieldType === 'attachment') {
      domOptions.allow_multi = false;
    }

    // Sequence
    let seqNo = 1;
    if (!this.childFieldEditMode) {
      const parentGroup = this.dataFieldHash[this.currentParentFieldId];
      if (parentGroup && parentGroup.children && parentGroup.children.length > 0) {
        const maxSeq = Math.max(...parentGroup.children.map((child: any) => child.seq_no || child.sequence_no || 0));
        seqNo = (maxSeq === -Infinity || isNaN(maxSeq) ? 0 : maxSeq) + 1;
      }
    } else {
      seqNo = this.selectedChildFieldDetails.seq_no || this.selectedChildFieldDetails.sequence_no || 1;
    }

    const fieldDefinition: any = {
      name: fieldName,
      display_name: displayName,
      data_category: dataCategory,
      data_type: fieldType,
      group_id: this.currentParentFieldId,
      custom_parameter_type: 4,
      seq_no: seqNo,
      dom_options: domOptions
    };

    if (formData.download_enabled !== undefined) {
      fieldDefinition.download = formData.download_enabled;
    }
    if (formData.add_to_filters !== undefined) {
      fieldDefinition.es_filter = formData.add_to_filters;
    }
    if (formData.add_to_cohorts !== undefined) {
      fieldDefinition.compare_filter = formData.add_to_cohorts;
    }
    if (formData.inbox_search_enabled !== undefined) {
      fieldDefinition.inbox_search_enabled = formData.inbox_search_enabled;
    }
    if (formData.show_in_inbox_detail !== undefined) {
      fieldDefinition.show_in_inbox_detail = formData.show_in_inbox_detail;
    }

    // Link datasource
    if (fieldType === 'link' && formData.link_urls) {
      const urls = String(formData.link_urls).split(',').map((url: string) => url.trim()).filter(Boolean);
      fieldDefinition.datasource = {
        type: 4,
        options: {
          values: urls
        }
      };
    }

    // List datasource (Individual / Data Mapping / API)
    if (fieldType === 'list') {
      const source = formData.list_data_source || 'individual';
      if (source === 'api') {
        fieldDefinition.datasource = {
          type: 2,
          options: {
            method: formData.list_api_method || 'GET',
            url: formData.list_api_url || '',
            data: {},
            value_field: formData.list_api_value_field || '',
            display_field: formData.list_api_display_field || '',
            is_json: true
          }
        };
      } else if (source === 'mapping') {
        fieldDefinition.datasource = {
          type: 1,
          options: {
            dataset_id: formData.list_dataset_id,
            value_field: formData.list_api_value_field || '',
            display_field: formData.list_api_display_field || ''
          }
        };
        if (formData.list_parent_field_id) {
          fieldDefinition.parent_field_id = formData.list_parent_field_id;
        }
      } else {
        const values = String(formData.list_individual_values || '')
          .split(',')
          .map((v: string) => v.trim())
          .filter(Boolean);
        fieldDefinition.datasource = {
          type: 4,
          options: {
            values
          }
        };
      }
    }

    // Nested group type (other custom group field_id)
    if (fieldType && (fieldType.indexOf('ltms_custom_field_') >= 0 || this.isGroupFieldType(fieldType))) {
      fieldDefinition.dom_options = fieldDefinition.dom_options || {};
      fieldDefinition.dom_options.group_to_render = fieldType;
      fieldDefinition.data_type = 'group';
    }

    if (this.childFieldEditMode && this.selectedChildFieldDetails.field_id) {
      fieldDefinition.field_id = this.selectedChildFieldDetails.field_id;
      const parentGroup = this.dataFieldHash[this.currentParentFieldId];
      if (parentGroup && parentGroup.children) {
        const oldData = _.filter(parentGroup.children, (d: any) => d.field_id === fieldDefinition.field_id);
        fieldDefinition.rules = oldData && oldData.length && oldData[0].rules && oldData[0].rules.length
          ? oldData[0].rules
          : (this.selectedChildFieldDetails.rules || []);
      } else if (this.selectedChildFieldDetails.rules) {
        fieldDefinition.rules = this.selectedChildFieldDetails.rules;
      }
    }

    delete fieldDefinition.access_key_rules;
    delete fieldDefinition.children;

    const payload: any = {
      brand_id: this.brandDetails._id,
      field_definitions: [fieldDefinition]
    };

    this.pagerBusy = true;
    const apiCall = this.childFieldEditMode && this.selectedChildFieldDetails.field_id
      ? this._settingsService.updateFieldDefinition(this.brandDetails._id, payload.field_definitions, null, null)
      : this._settingsService.insertFieldDefinitions(this.brandDetails._id, payload.field_definitions);

    apiCall
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          this.pagerBusy = false;
          if (response && response.code === 0) {
            if (this.childFieldEditMode) {
              Helpers.smallAlertBox('Success', 'Data Field updated successfully.', 'Success');
            } else {
              Helpers.smallAlertBox('Success', 'Data Field added successfully.', 'Success');
            }
            this.closeChildFieldModal();
            this.afterUpdateBrandConfig();
          } else if (response && response.code === 4021) {
            Helpers.smallAlertBox('Warning', 'Data field name already exists, please enter another name.', 'warn');
          } else {
            Helpers.smallAlertBox('Error', response?.error_message || 'Error while saving data field.', 'error');
          }
        },
        error: (err: any) => {
          this.pagerBusy = false;
          console.error('Error saving child field:', err);
          Helpers.smallAlertBox('Error', 'Error while saving data field.', 'error');
        }
      });
  }

  /** True when field_type option refers to another custom group */
  private isGroupFieldType(fieldType: string): boolean {
    if (!fieldType) {
      return false;
    }
    const parent = this.dataFieldHash[fieldType];
    return !!(parent && parent.custom_parameter_type === 4 && !parent.group_id);
  }

  /**
   * Toggle visibility
   */
  toggleVisibility(field: DataField) {
    field.visible = !field.visible;
    this.isSaveEnabled = true;
  }

  /**
   * Delete field (parent group or child field)
   * Matching old dashboard logic: deleteParent or deleteChild
   */
  deleteField(field: DataField) {
    if(this.readonly){
      Helpers.smallAlertBox('Access denied',"You do not have write permission","info",1500)
      return
    }
    // Determine if this is a parent group or child field
    const isParentGroup = !field.group_id || field.group_id === null;
    const confirmationMsg = isParentGroup
      ? 'Are you sure you want to delete this Custom Group and Rules related to it?'
      : 'Are you sure you want to delete this Custom Field?';

    this.showConfirmationBox('Confirmation', confirmationMsg, (answer: boolean) => {
      if (answer) {
        const deleteType = isParentGroup ? 'deleteParent' : 'deleteChild';
        const fieldId = field.field_id;
        const arrayOfFieldIds: string[] = [];

        if (isParentGroup) {
          // For parent groups, collect all children field IDs and clean related ui_rules
          this.retrieveRuleToDelete(fieldId);
          if (field.children && field.children.length > 0) {
            field.children.forEach((child: DataField) => {
              if (child.group_id === fieldId) {
                arrayOfFieldIds.push(child.field_id);
              }
            });
          }
          // Add parent field ID
          arrayOfFieldIds.push(fieldId);
        } else {
          // For child fields, only add the child field ID
          arrayOfFieldIds.push(fieldId);
        }

        // Call API to delete
        this._settingsService.removeFieldDefinitions(
          this.brandDetails._id,
          fieldId,
          deleteType,
          arrayOfFieldIds
        )
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: (response: any) => {
              if (response && response.code === 0) {
                this.toastService.showPopup('SUCCESS', 'Success', 'Removed successfully.', 3000);
                // Refresh the data field list
                this.afterUpdateBrandConfig();
              } else if (response && response.code === 4021) {
                this.toastService.showPopup('WARNING', 'Warning', 'No changes made.', 3000);
              } else {
                if (response && response.error_message) {
                  this.toastService.showPopup('ERROR', 'Error', response.error_message, 3000);
                } else {
                  this.toastService.showPopup('ERROR', 'Error', 'Problem while deleting field.', 3000);
                }
              }
            },
            error: (err: any) => {
              console.error('Error deleting field:', err);
              this.toastService.showPopup('ERROR', 'ERROR', 'Error while deleting field.', 3500);
            }
          });
      }
    });
  }

  /**
   * Remove ui_rules tied to a deleted parent group (matching old retrieveRuleTodelete)
   */
  private retrieveRuleToDelete(parentFieldId: string) {
    if (!_.get(this.brandDetails, 'ui_rules')) {
      return;
    }
    const uiRulesWorkflow = Helpers.deepClone(this.brandDetails.ui_rules) || [];
    const uiRuleToUpdate: any[] = [];
    _.each(uiRulesWorkflow, (rule: any) => {
      if (_.get(rule, 'workflow')) {
        const workflowData = _.get(rule, 'workflow')[0]?.actions || [];
        let keepRule = true;
        _.each(workflowData, (action: any) => {
          if (_.get(action, 'options.group_id') == parentFieldId) {
            keepRule = false;
          }
        });
        if (keepRule) {
          uiRuleToUpdate.push(rule);
        }
      } else {
        uiRuleToUpdate.push(rule);
      }
    });
    this._settingsService.updateUIRules(this.brandId, uiRuleToUpdate, true)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => { /* brand refresh happens via field delete afterUpdateBrandConfig */ },
        error: (err: any) => console.error('Error cleaning ui_rules for deleted group:', err)
      });
  }

  /**
   * Toggle rule
   */
  enableRule(rule: any) {
    if(this.readonly){
      Helpers.smallAlertBox('Access denied',"You do not have write permission","info",1500)
      return
    }
    rule.is_enabled = !rule.is_enabled;
    this.updateRule('updateRule', rule, true, true);
  }
 
  /**
   * Edit rule
   */
  ruleToReplace: any = null;
  ruleToEditId: string | null = null;
  editRule(rule: Rule) {
    this.ruleEditMode = true;
    const clonedRule = _.cloneDeep(rule);
    this.selectedRuleDetails = clonedRule;
    this.ruleToReplace = _.cloneDeep(rule);
    this.ruleToEditId = (clonedRule as any).uuid || clonedRule.id || null;
    this.dataRuleType = clonedRule.type || '';
    this.lwDataFieldWorkflowActionsList = 0;

    // Matching old notes remapping for visibility/read-only edit
    if ((this.dataRuleType === 'visibility' || this.dataRuleType === 'read-only') &&
      (clonedRule as any).applies_to === 'notes') {
      (clonedRule as any).applies_to = 'ltms_custom_field_group_notes';
    }

    this.mapForRuletype();
    this.populateFieldDefsMap();
    this.updateRuleSetExcludeByType(this.dataRuleType);

    this.getWorkflowRules().then(() => {
      this.initializeEditRuleModal(clonedRule);
    });
  }

  /**
   * Initialize edit rule modal
   */
  private initializeEditRuleModal(rule: Rule) {
    this.ruleModalSchema = _.cloneDeep(dataFieldRuleSchema);
    this.ruleModalSchema.title = 'Edit Rule';
    this.ruleModalSchema.basic.properties.type.disabled = true;
    this.applyRuleTypeOptionsMap();

    if (this.ltmsEventList && this.ltmsEventList.length > 0) {
      this.ruleModalSchema.basic.properties.event_name.options = this.ltmsEventList;
    }

    let appliesTo = '';
    let appliesToChildFieldIds: any[] = [];
    let eventName = '';
    let conditions: any[] = [];

    // Prefer top-level conditions, then workflow.0.conditions (mandatory / stored shape)
    if (rule.conditions && Array.isArray(rule.conditions) && rule.conditions.length) {
      conditions = _.cloneDeep(rule.conditions);
    } else if (rule.conditions && typeof rule.conditions === 'object' && !Array.isArray(rule.conditions)) {
      conditions = [_.cloneDeep(rule.conditions)];
    } else if (_.get(rule, 'workflow.0.conditions')) {
      const workflowConditions = _.get(rule, 'workflow.0.conditions');
      conditions = Array.isArray(workflowConditions)
        ? _.cloneDeep(workflowConditions)
        : [_.cloneDeep(workflowConditions)];
    }

    const defaultObject = _.cloneDeep(dataFieldRuleSchema.conditions.properties.dataObject);
    if (conditions.length === 0) {
      conditions = [defaultObject];
    } else {
      // Drop trailing empty/incomplete rows then append a blank editor row
      conditions = conditions.filter((c: any) => c && (c.ruleset_id || c.unique_id || c.field_id || c.operator_id));
      if (conditions.length === 0) {
        conditions = [defaultObject];
      } else {
        conditions.push(defaultObject);
      }
    }

    if ((rule as any).applies_to) {
      appliesTo = (rule as any).applies_to;
    } else if (_.get(rule, 'workflow.0.actions.0.options.group_id')) {
      appliesTo = _.get(rule, 'workflow.0.actions.0.options.group_id');
    }

    if ((rule as any).applies_to_child_field_ids) {
      appliesToChildFieldIds = Array.isArray((rule as any).applies_to_child_field_ids)
        ? (rule as any).applies_to_child_field_ids
        : [(rule as any).applies_to_child_field_ids];
    } else if (_.get(rule, 'workflow.0.actions.0.options.child_group_id')) {
      const childIds = _.get(rule, 'workflow.0.actions.0.options.child_group_id');
      appliesToChildFieldIds = Array.isArray(childIds) ? childIds : [childIds];
    }

    if (_.get(this.dataFieldHash[appliesTo], 'custom_parameter_type') == 4) {
      this.customType = 4;
    } else {
      this.customType = null;
    }

    if ((rule as any).event_name) {
      eventName = (rule as any).event_name;
    }

    if (appliesTo) {
      this.appliesToChildFieldOptions = this.getAppliesToChildOptions(appliesTo);
    }

    this.ruleDefaultValues = {
      uuid: (rule as any).uuid || rule.id || '',
      created_date: rule.created_date ? moment(rule.created_date).toISOString() : '',
      is_enabled: rule.is_enabled ? 'true' : 'false',
      name: rule.name || '',
      type: this.dataRuleType || '',
      event_name: eventName,
      applies_to: appliesTo,
      applies_to_child_field_ids: appliesToChildFieldIds,
      description: rule.description || '',
      ignore_for_ui_rule: (rule as any).ignore_for_ui_rule || false,
      conditions: conditions
    };

    this.updateRuleModalSchema();
    this.updateSchemaForRuleType(this.dataRuleType);

    this.isRuleModalOpen = true;
    this.isAdvancedRuleModalOpen = true;
  }

  /**
   * Update schema based on rule type
   */
  private updateSchemaForRuleType(ruleType: string) {
    if (!this.ruleModalSchema.basic || !this.ruleModalSchema.basic.properties) {
      return;
    }

    this.updateRuleSetExcludeByType(ruleType);

    // Show/hide event field based on rule type
    if (this.ruleModalSchema.basic.properties.event_name) {
      if (ruleType === 'mandatory') {
        this.ruleModalSchema.basic.properties.event_name.hidden = false;
        this.ruleModalSchema.basic.properties.event_name.required = true;
        this.ruleModalSchema.basic.properties.event_name.errorMsg = 'Event is mandatory.';
      } else {
        this.ruleModalSchema.basic.properties.event_name.hidden = true;
        this.ruleModalSchema.basic.properties.event_name.required = false;
      }
    }

    // Show/hide applies_to_child_field_ids based on applies_to selection
    if (this.ruleDefaultValues.applies_to) {
      this.appliesToChildFieldOptions = this.getAppliesToChildOptions(this.ruleDefaultValues.applies_to);
      if (this.ruleModalSchema.basic.properties.applies_to_child_field_ids) {
        this.ruleModalSchema.basic.properties.applies_to_child_field_ids.options = this.appliesToChildFieldOptions;
      }
      if (this.appliesToChildFieldOptions && this.appliesToChildFieldOptions.length > 0 && ruleType !== 'mandatory') {
        if (this.ruleModalSchema.basic.properties.applies_to_child_field_ids) {
          this.ruleModalSchema.basic.properties.applies_to_child_field_ids.hidden = false;
          this.ruleModalSchema.basic.properties.applies_to_child_field_ids.required = true;
          this.ruleModalSchema.basic.properties.applies_to_child_field_ids.errorMsg = 'Applies to field is mandatory.';
          this.ruleModalSchema.basic.properties.applies_to_child_field_ids.showLabel = true;
        }
      } else {
        if (this.ruleModalSchema.basic.properties.applies_to_child_field_ids) {
          this.ruleModalSchema.basic.properties.applies_to_child_field_ids.hidden = true;
          this.ruleModalSchema.basic.properties.applies_to_child_field_ids.required = false;
        }
      }
    }
  }

  /**
   * Close rule modal
   */
  closeRuleModal() {
    this.isRuleModalOpen = false;
    this.isAdvancedRuleModalOpen = false;
    this.ruleEditMode = false;
    this.selectedRuleDetails = {};
    this.ruleDefaultValues = {};
    this.ruleModalSchema = {};
    this.dataRuleType = '';
    this.appliesToChildFieldOptions = [];
    this.ruleToReplace = null;
    this.ruleToEditId = null;
    this.customType = null;
  }

  /**
   * Handle rule form submit
   * Matching old submitForm for operationOn == "rule"
   */
  parentFieldId: any = null;
  onRuleFormSubmit(formData: any) {
    if(this.readonly){
      Helpers.smallAlertBox('Access denied',"You do not have write permission","info",1500)
      return
    }
    if (!formData) {
      return;
    }
    if (formData && !('event_name' in formData) && formData['type'] == 'mandatory') {
      Helpers.smallAlertBox('Error', 'Event required for the rule.', 'error');
      return;
    }
    if (formData.type === 'mandatory' && !formData.event_name) {
      Helpers.smallAlertBox('Error', 'Event required for the rule.', 'error');
      return;
    }

    this.dataRuleType = formData.type || this.dataRuleType;

    // Validate rule name for duplicates (case-sensitive to match old checkForDuplicate for rules)
    if (this.checkForDuplicateRuleName(formData.name || '', formData.uuid)) {
      Helpers.smallAlertBox('Warning', 'Rule name already exists, please enter another name.', 'warn');
      return;
    }

    // Expand "Select All" and validate duplicate applies-to for visibility/read-only
    formData = this.normalizeAppliesToChildSelection(formData);
    this.checkExistingRules(
      formData.applies_to_child_field_ids?.length
        ? formData.applies_to_child_field_ids
        : formData.applies_to,
      (isExist: boolean) => {
        if (isExist && formData.type !== 'mandatory') {
          Helpers.smallAlertBox('Error', 'Rules for this applies to exist', 'error', 3500);
          return;
        }

        const ruleData = this.prepareRuleData(formData);
        this.parentFieldId = formData.applies_to || null;

        if (this.ruleEditMode) {
          Helpers.showConfirmationBox(
            'Confirmation',
            'Please note that this change will Override the previous configuration. Are you sure, you want to proceed?',
            (answer: boolean) => {
              if (answer) {
                this.checkForDuplicate('rule', this.parentFieldId, formData, (hasDuplicateName: boolean) => {
                  if (hasDuplicateName) {
                    Helpers.smallAlertBox('Warning', 'Rule name already exists, please enter another name.', 'warn');
                  } else {
                    this.updateRule('updateRule', ruleData);
                  }
                });
              }
            }
          );
        } else {
          this.checkForDuplicate('rule', this.parentFieldId, formData, (hasDuplicateName: boolean) => {
            if (hasDuplicateName) {
              Helpers.smallAlertBox('Warning', 'Rule name already exists, please enter another name.', 'warn');
            } else {
              this.insertRule(ruleData);
            }
          });
        }
      }
    );
  }

  /** Expand Select All and strip the "all" token from child ids */
  private normalizeAppliesToChildSelection(formData: any): any {
    const data = _.cloneDeep(formData);
    let childIds = data.applies_to_child_field_ids;

    // Multi-select may store option objects — normalize to ids
    if (Array.isArray(childIds)) {
      childIds = childIds.map((item: any) => {
        if (item == null) {
          return null;
        }
        if (typeof item === 'string' || typeof item === 'number') {
          return item;
        }
        return item.id || item.value || null;
      }).filter((id: any) => id != null && id !== '');
    }

    if (childIds && Array.isArray(childIds) && childIds.indexOf('all') > -1) {
      const childOptions = this.getAppliesToChildOptions(data.applies_to);
      childIds = childOptions
        .filter((opt: any) => opt.id !== 'all')
        .map((opt: any) => opt.id);
    }

    data.applies_to_child_field_ids = childIds || [];
    return data;
  }

  /**
   * Matching old checkExistingRules — block overlapping visibility/read-only applies_to
   */
  private checkExistingRules(selectedValues: any, cb: (isExist: boolean) => void) {
    let isExist = false;
    if (this.dataFieldRulesBackup && this.dataFieldRulesBackup.length && selectedValues) {
      const isStaticTarget = !Array.isArray(selectedValues) &&
        this.dataFieldHash[selectedValues] &&
        this.dataFieldHash[selectedValues].custom_parameter_type != 4;

      if (isStaticTarget) {
        _.each(this.dataFieldRulesBackup, (rule: any) => {
          if (rule.applies_to && String(rule.applies_to).indexOf(selectedValues) > -1 &&
            rule.type == this.dataRuleType && this.dataRuleType != 'mandatory' &&
            rule.uuid != this.ruleToEditId) {
            isExist = true;
          }
        });
      } else {
        _.each(this.dataFieldRulesBackup, (rule: any) => {
          const commonIds = this.commonElementsArrayFetch(selectedValues, rule.applies_to_child_field_ids);
          if (commonIds && commonIds.length && rule.type == this.dataRuleType &&
            this.dataRuleType != 'mandatory' && rule.uuid != this.ruleToEditId) {
            isExist = true;
          }
        });
      }
    }
    cb(isExist);
  }

  private commonElementsArrayFetch(selectVal: any, activeChildId: any): any[] {
    if (!(selectVal && activeChildId)) {
      return [];
    }
    const selectValArray = Array.isArray(selectVal) ? selectVal : [selectVal];
    const activeIdArray = Array.isArray(activeChildId) ? activeChildId : [activeChildId];
    return _.intersection(selectValArray, activeIdArray);
  }

  /**
   * Check for duplicate rule name
   */
  private checkForDuplicateRuleName(ruleName: string, excludeUuid?: string): boolean {
    if (!ruleName) {
      return false;
    }
    // Matching old rule duplicate check — exact (case-sensitive) name match
    return this.dataFieldRulesBackup.some((rule: Rule) => {
      const ruleUuid = (rule as any).uuid || rule.id;
      return rule.name === ruleName && ruleUuid !== excludeUuid;
    });
  }

  checkForDuplicate(type, parentFieldId, objectToUpdate, cb) {
    var matchedField: any = null;
    var uniqueField: string = "field_id";
    if (!objectToUpdate || !objectToUpdate.name) {
      cb(false);
      return;
    }
    var nameToUpdate: string = objectToUpdate.name.toLowerCase();
    switch (type) {
      case "dataField":
        var parentGroup = this.dataFieldHash[parentFieldId];
        if (parentGroup) {
          matchedField = _.filter(parentGroup["children"], function (field) {
            if (field["name"]) {
              return (field["name"].toLowerCase().indexOf(nameToUpdate) >= 0);
            } else {
              return false;
            }
          });
        }
        break;
      case "dataGroup":
        matchedField = _.filter(this.dataFieldHash, function (field) {
          if (field && field.name && objectToUpdate && objectToUpdate.name) {
            return field.name.toLowerCase() == objectToUpdate.name.toLowerCase();
          } else if (field.name && objectToUpdate.name) {
            return field.name == objectToUpdate.name;
          } else {
            return false;
          }
        });
        break;
      case "rule":
        uniqueField = "uuid";
        matchedField = _.filter(this.dataFieldRulesBackup, function (field) {
          return field.name == objectToUpdate.name;
        });
        break;
    }
    if (matchedField && matchedField.length && matchedField[0][uniqueField] != objectToUpdate[uniqueField]) {
      cb(true);
    } else {
      cb(false);
    }
  }


  /**
         * prepareDataFieldToUpdateRule : Method to prepare data field/custom parameter for the rule
         *                                This is basically for the visibility/read-only rule.
         */
  prepareDataFieldToUpdateRule(rule: any, ruleType: string) {
    var appliesTo = rule["applies_to"] || _.get(rule, "workflow.0.actions.0.options.group_id");
    // Get the field for which rule has been applied
    var fieldToUpdate: any = null;
    var ruleToInsert = Helpers.deepClone(rule);
    // In case is_enabled not defined in rule then set as true by default
    ruleToInsert["is_enabled"] = (_.hasIn(rule, "is_enabled")) ? _.get(rule, "is_enabled") : true;
    ruleToInsert["type"] = ruleToInsert["type"];
    ruleToInsert["ignore_for_ui_rule"] = _.hasIn(ruleToInsert, "ignore_for_ui_rule") ? ruleToInsert["ignore_for_ui_rule"] : false;

    // Delete the fields which are not required
    // delete ruleToInsert["type"];
    // delete ruleToInsert["applies_to"];
    delete ruleToInsert["event_name"];

    if ((appliesTo == "ltms_custom_field_group_notes" && (ruleType == "visibility" || ruleType == "read-only"))) {

      fieldToUpdate = Helpers.deepClone(this.dataFieldHash["notes"]);

      rule["applies_to"] = "notes";
      ruleToInsert["applies_to"] = "notes";
    } else {
      var dataFieldFromFieldDefinitions = Helpers.deepClone(this.dataFieldHash[appliesTo]);
      if (rule["applies_to_child_field_ids"] && rule["applies_to_child_field_ids"].length && dataFieldFromFieldDefinitions.children && dataFieldFromFieldDefinitions.children.length) {
        fieldToUpdate = _.filter(dataFieldFromFieldDefinitions.children, (field: any) => {
          // delete ruleToInsert.applies_to_child_field_ids
          field["rules"] = [ruleToInsert];
          return field;
        })
      } else {
        fieldToUpdate = Helpers.deepClone(this.dataFieldHash[appliesTo]);
        if (fieldToUpdate) {
          fieldToUpdate.rules = [ruleToInsert];
        } else {
          fieldToUpdate = dataFieldFromFieldDefinitions;
          if (fieldToUpdate) {
            fieldToUpdate.rules = [ruleToInsert];
          }
        }
      }
    }

    if (fieldToUpdate && fieldToUpdate["children"]) {
      // Remove children, as we don't store it in db.
      delete fieldToUpdate["children"];
    }


    // if (Utils.getProp(fieldToUpdate, "rules.0")) {
    //     // If there is already a rule in field then push new one into it.

    //     // Check if rule already exists(In case of rule update operation)
    //     var existingRuleIndex = _.findIndex(fieldToUpdate.rules, function(ruleInFieldToUpdate) {
    //         return ruleInFieldToUpdate.applies_to == rule.applies_to;
    //     });
    //     // need to comment will handle in API 
    //     if (existingRuleIndex > -1) {
    //         fieldToUpdate.rules[existingRuleIndex] = ruleToInsert;
    //     } else {
    // fieldToUpdate.rules.push(ruleToInsert);
    //     }
    // } else {
    // No existing rule
    // }
    return fieldToUpdate;
  }
  /**
   * Prepare rule data for API
   */
  private prepareRuleData(formData: any): any {
    // Filter incomplete trailing condition rows
    let conditions = Array.isArray(formData.conditions) ? formData.conditions : [];
    conditions = conditions.filter((c: any) =>
      c && !c.disabled && (c.ruleset_id || c.unique_id) && c.operator_id
    );

    const ruleData: any = {
      name: formData.name,
      type: formData.type,
      description: formData.description || '',
      is_enabled: this.ruleEditMode
        ? (formData.is_enabled === 'true' || formData.is_enabled === true)
        : true,
      ignore_for_ui_rule: formData.ignore_for_ui_rule || false,
      conditions: conditions
    };

    if (this.ruleEditMode && formData.uuid) {
      ruleData.uuid = formData.uuid;
    }
    if (formData.created_date) {
      ruleData.created_date = formData.created_date;
    }

    if (formData.type === 'mandatory' && formData.event_name) {
      ruleData.event_name = formData.event_name;
    }

    if (formData.applies_to) {
      ruleData.applies_to = formData.applies_to;
    }

    if (formData.applies_to_child_field_ids && Array.isArray(formData.applies_to_child_field_ids) &&
      formData.applies_to_child_field_ids.length > 0) {
      ruleData.applies_to_child_field_ids = formData.applies_to_child_field_ids.filter((id: any) => id && id !== 'all');
    }

    const workflow: any = {
      conditions: conditions
    };

    if (formData.applies_to) {
      const actionOptions: any = {
        group_id: formData.applies_to
      };
      if (ruleData.applies_to_child_field_ids?.length) {
        actionOptions.child_group_id = ruleData.applies_to_child_field_ids;
      }

      if (formData.applies_to === 'ltms_custom_field_group_notes') {
        workflow.actions = [{
          action: 'openLWNotes',
          options: actionOptions
        }];
      } else {
        workflow.actions = [{
          action: 'openPopup',
          options: actionOptions
        }];
      }
    }

    ruleData.workflow = [workflow];

    return ruleData;
  }


  /**
   * prepareRuleForUpdate : Method which will prepare rule based on the schema in db.
   * @param  {object} rule: Rule which needs to be either insert or update
   */

  private prepareRuleForUpdate(rule: any, cb: any) {
    const conditions: any = rule['conditions'] || _.get(rule, 'workflow.0.conditions') || [];
    let actionType = _.get(rule, 'applies_to') || _.get(rule, 'workflow.0.actions.0.options.group_id');
    let workflow: any = {};
    if (actionType == 'ltms_custom_field_group_notes') {
      workflow = {
        conditions: conditions,
        actions: [{
          action: 'openLWNotes',
          options: {
            group_id: actionType
          }
        }]
      };
    } else {
      workflow = {
        conditions: conditions,
        actions: [{
          action: 'openPopup',
          options: {
            group_id: actionType
          }
        }]
      };
    }
    const objectToUpdate = Helpers.deepClone(rule);
    // Prefer existing workflow when enable/delete and applies_to was already nested
    if ((!actionType || !conditions) && _.get(rule, 'workflow.0')) {
      objectToUpdate['workflow'] = Helpers.deepClone(rule.workflow);
    } else {
      objectToUpdate['workflow'] = [workflow];
    }
    delete objectToUpdate['conditions'];
    delete objectToUpdate['events'];
    delete objectToUpdate['applies_to'];

    cb(objectToUpdate);
  }

  /**
   * Insert new rule
   */
  private insertRule(ruleData: any) {
    this.pagerBusy = true;
    if (ruleData.type === 'visibility' || ruleData.type === 'read-only') {
      const dataHash = this.prepareDataFieldToUpdateRule(ruleData, ruleData.type);
      if (_.get(dataHash, 'custom_parameter_type') != 4 && !Array.isArray(dataHash)) {
        this.updateStaticRules('insert', ruleData);
      } else {
        this.updateFieldRule('insert', dataHash);
      }
    } else if (ruleData.type === 'mandatory') {
      ruleData['is_enabled'] = true;
      const ruleForPrep: any = {
        ...ruleData,
        conditions: _.get(ruleData, 'workflow.0.conditions') || ruleData.conditions || [],
        applies_to: ruleData.applies_to || _.get(ruleData, 'workflow.0.actions.0.options.group_id')
      };
      this.prepareRuleForUpdate(ruleForPrep, (rule: any) => {
        this._settingsService.insertUIRules(this.brandId, [rule])
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: (response: any) => {
              this.pagerBusy = false;
              if (response && !response.error_message) {
                if (response.code === 0) {
                  this.closeRuleModal();
                  Helpers.smallAlertBox('Success', 'Rule added successfully.', 'Success');
                  this.afterUpdateBrandConfig();
                } else if (response.code === 4021) {
                  Helpers.smallAlertBox('Error', 'Rule already exists.', 'error');
                } else {
                  Helpers.smallAlertBox('Error', 'Problem while adding Rule.', 'error');
                }
              } else {
                Helpers.smallAlertBox('Error', 'Problem while adding Rule.', 'error');
              }
            },
            error: (error: any) => {
              this.pagerBusy = false;
              Helpers.smallAlertBox('Error', 'Problem while adding Rule.', 'error');
              console.error('Error inserting rule:', error);
            }
          });
      });
    } else {
      this.pagerBusy = false;
      Helpers.smallAlertBox('Error', 'Incorrect rule to insert.', 'error', 3000);
    }
  }

  /**
   * Update existing rule
   */
  mode: any = null;
  customType: any = null;
  private updateRule(mode: any, ruleData: any, isEnableFlag?: boolean, isShowLoading?: boolean) {
    if (isShowLoading) {
      this.pagerBusy = true;
    }

    if (_.hasIn(ruleData, '$$hashKey')) {
      delete ruleData['$$hashKey'];
    }

    if (ruleData.type === 'visibility' || ruleData.type === 'read-only') {
      const dataField = this.prepareDataFieldToUpdateRule(ruleData, ruleData.type);
      if (mode == 'deleteRule') {
        if (_.get(dataField, 'custom_parameter_type') != 4 && !Array.isArray(dataField)) {
          this.updateStaticRules('delete', ruleData);
        } else {
          this.updateFieldRule('delete', dataField);
        }
      } else if (mode == 'updateRule' && (_.get(dataField, 'rules') || Array.isArray(dataField)) && !this.ruleEditMode) {
        if (_.get(dataField, 'custom_parameter_type') != 4 && !Array.isArray(dataField)) {
          this.updateStaticRules('update', ruleData);
        } else {
          if (_.hasIn(dataField, '$$hashKey')) {
            delete dataField['$$hashKey'];
          }
          this.updateFieldRule('update', dataField);
        }
      } else {
        if (_.get(dataField, 'custom_parameter_type') != 4 && !Array.isArray(dataField)) {
          if (this.customType) {
            const dataFieldRuleToDelete = this.prepareDataFieldToUpdateRule(this.ruleToReplace, ruleData.type);
            this.updateFieldRule('delete', dataFieldRuleToDelete, true);
            this.updateStaticRules('insert', ruleData);
          } else {
            this.updateStaticRules('update', ruleData);
          }
        } else {
          if (this.customType) {
            this.updateFieldRule('update', dataField);
          } else {
            this.updateStaticRules('delete', this.ruleToReplace, true);
            this.updateFieldRule('insert', dataField);
          }
        }
      }
    } else if (ruleData.type === 'mandatory') {
      const ruleForPrep: any = {
        ...ruleData,
        conditions: ruleData.conditions || _.get(ruleData, 'workflow.0.conditions') || [],
        applies_to: ruleData.applies_to || _.get(ruleData, 'workflow.0.actions.0.options.group_id')
      };
      this.prepareRuleForUpdate(ruleForPrep, (rule: any) => {
        let isDeleteFlag = false;
        let uiRulesWorkflowToUpdate: any[] = [];
        if (mode == 'deleteRule') {
          isDeleteFlag = true;
          const uiRulesWorkflowRules = _.cloneDeep(this.brandDetails.ui_rules) || [];
          _.each(uiRulesWorkflowRules, (ruleTodelete: any) => {
            if (rule['uuid'] != ruleTodelete['uuid']) {
              uiRulesWorkflowToUpdate.push(ruleTodelete);
            }
          });
        } else if (isEnableFlag) {
          const uiRulesWorkflowRules = Helpers.deepClone(this.brandDetails.ui_rules) || [];
          _.each(uiRulesWorkflowRules, (mandatoryRuleToUpdate: any) => {
            if (rule['uuid'] == mandatoryRuleToUpdate['uuid']) {
              mandatoryRuleToUpdate['is_enabled'] = rule['is_enabled'];
              uiRulesWorkflowToUpdate.push(mandatoryRuleToUpdate);
            } else {
              uiRulesWorkflowToUpdate.push(mandatoryRuleToUpdate);
            }
          });
        } else {
          uiRulesWorkflowToUpdate.push(rule);
        }

        this._settingsService.updateUIRules(this.brandId, uiRulesWorkflowToUpdate, isDeleteFlag)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: (response: any) => {
              this.pagerBusy = false;
              if (response) {
                if (response.data && response.data.code == 4021) {
                  Helpers.smallAlertBox('Warning', 'Rule already exists or updated.', 'warn');
                } else if (response.data && response.data.error_message) {
                  Helpers.smallAlertBox('Error', response.data.errors, 'error');
                } else if (response.code == 0 || (response.data && response.data.code == 0)) {
                  this.closeRuleModal();
                  if (mode == 'deleteRule') {
                    Helpers.smallAlertBox('Success', 'Rule deleted successfully.', 'Success');
                  } else {
                    Helpers.smallAlertBox('Success', 'Rule updated successfully.', 'Success');
                  }
                  this.afterUpdateBrandConfig();
                } else {
                  Helpers.smallAlertBox('Error', 'Problem while updating rule.', 'error');
                }
              } else {
                Helpers.smallAlertBox('Error', 'Problem while updating rule.', 'error');
              }
            },
            error: () => {
              this.pagerBusy = false;
              Helpers.smallAlertBox('Error', 'Problem while updating rule.', 'error');
            }
          });
      });
    } else {
      this.pagerBusy = false;
      Helpers.smallAlertBox('Error', 'Incorrect rule to update.', 'error', 3000);
    }
  }


  updateStaticRules(type: any, staticRule: any, isDynamicTransition?: boolean) {
    var accordionsType = 2; // For static accordions rule to update .
    if (staticRule && !_.isEmpty(staticRule)) {
      if (type == 'insert') {
        staticRule["is_enabled"] = false;
      }
      this.pagerBusy = true;
      this._settingsService.updateAccodionsList(this.brandId, null, staticRule, accordionsType, type)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (response: any) => {
            this.pagerBusy = false;
            if (response && !response.error_message) {
              if (response.code == 0) {
                this.closeRuleModal();
                if (!isDynamicTransition) {
                  if (type == 'delete') {
                    Helpers.smallAlertBox('Success', 'Rule deleted successfully.', 'Success');
                  } else if (type == 'insert') {
                    Helpers.smallAlertBox('Success', 'Rule saved successfully, please activate from the list.', 'Success');
                  } else {
                    Helpers.smallAlertBox('Success', 'Rule updated successfully.', 'Success');
                  }
                }
                this.afterUpdateBrandConfig();
              } else if (response.code == 4021 || response.code == 4022) {
                Helpers.smallAlertBox('Warning', 'No changes made.', 'warn');
              } else {
                Helpers.smallAlertBox('Error', 'Problem while updating field.', 'error');
              }
            } else if (response && response.code == 4022) {
              Helpers.smallAlertBox('Warning', 'No changes made.', 'warn');
            } else {
              Helpers.smallAlertBox('Error', 'Problem while updating field.', 'error');
            }
          },
          error: (error: any) => {
            this.pagerBusy = false;
            Helpers.smallAlertBox('Error', 'Problem while updating field.', 'error');
            console.error('Error updating field:', error);
          }
        });
    }
  }

  updateFieldRule(type: any, dataHash: any, isStaticFieldTrans?: boolean) {
    if (dataHash && (!_.isEmpty(dataHash) || Array.isArray(dataHash))) {
      if (_.hasIn(dataHash, '$$hashKey')) {
        delete dataHash['$$hashKey'];
      }
      let dataHashs: any[];
      let rule: any;
      if (!Array.isArray(dataHash)) {
        dataHashs = [dataHash];
        rule = dataHash.rules && dataHash.rules[0];
      } else {
        dataHashs = dataHash;
        rule = dataHash[0] && dataHash[0].rules && dataHash[0].rules[0];
      }
      this.pagerBusy = true;
      this._settingsService.updateFieldDefinition(this.brandId, dataHashs, type, rule)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (response: any) => {
            this.pagerBusy = false;
            if ((response && response.code == 0) || (response?.data && !response.data.error_message && response.data.code == 0)) {
              this.closeRuleModal();
              if (!isStaticFieldTrans) {
                if (type == 'delete') {
                  Helpers.smallAlertBox('Success', 'Rule deleted successfully', 'Success');
                } else {
                  Helpers.smallAlertBox('Success', 'Rule updated successfully.', 'Success');
                }
              }
              this.afterUpdateBrandConfig();
            } else if (response?.data?.code == 4021 || response?.code == 4021) {
              Helpers.smallAlertBox('Error', 'Data rule already updated.', 'error');
              this.closeRuleModal();
              this.afterUpdateBrandConfig();
            } else {
              Helpers.smallAlertBox('Error', 'Problem while updating rule.', 'error');
            }
          },
          error: (error: any) => {
            this.pagerBusy = false;
            Helpers.smallAlertBox('Error', 'Problem while updating rule.', 'error');
            console.error('Error updating rule:', error);
          }
        });
    } else {
      this.pagerBusy = false;
      Helpers.smallAlertBox('Error', 'Invalid ' + type + '.', 'error');
    }
  }

  /**
   * Delete rule
   */

  deleteRule(ruleToDelete: any) {
    if(this.readonly){
      Helpers.smallAlertBox('Access denied',"You do not have write permission","info",1500)
      return
    } 
    Helpers.showConfirmationBox("Confirmation", "Are you sure you want to delete this rule?", (answer) =>{
      if (answer) {
          ruleToDelete["is_deleted"] = true;
          this.prepareRuleForUpdate(ruleToDelete, (rule)=> {
              this.updateRule('deleteRule', ruleToDelete);
          });
      }
  });
  }

  /**
   * Previous page
   */
  previousPage() {
    if (this.rulesPageNo > 1) {
      this.isNextCtrl = false;
      this.rulesPageNo--;

      // Use appropriate pagination function based on filter state
      if (this.filterOrAll === 1) {
        this.getRulefilterWises();
      } else {
        this.getRulePageWises();
      }
    }
  }

  /**
   * Next page
   */
  nextPage() {
    if (!this.disableNext) {
      this.isNextCtrl = true;
      this.rulesPageNo++;

      // Use appropriate pagination function based on filter state
      if (this.filterOrAll === 1) {
        this.getRulefilterWises();
      } else {
        this.getRulePageWises();
      }
    }
  }

  /**
   * Save the data fields configuration
   */
  saveConfiguration() {
    if (this.isSaving) return;

    this.isSaving = true;

    // Prepare field definitions for update
    const fieldDefinitions: any[] = [];

    // Convert dataFieldHash back to array format
    Object.keys(this.dataFieldHash).forEach((key) => {
      const field = this.dataFieldHash[key];
      if (!field.is_deleted) {
        if (field.children && field.children.length) {
          // Add parent
          const parentField = { ...field };
          delete parentField.children;
          fieldDefinitions.push(parentField);
          // Add children
          field.children.forEach((child: any) => {
            if (!child.is_deleted) {
              fieldDefinitions.push(child);
            }
          });
        } else {
          fieldDefinitions.push(field);
        }
      }
    });

    // Update field definitions
    this._settingsService.updateFieldDefinition(this.brandId, fieldDefinitions)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          if (res && res.code == 0) {
            this.toastService.showPopup('SUCCESS', 'Success', 'Data fields settings saved successfully.', 3000);
            this.isSaveEnabled = false;
            this.afterUpdateBrandConfig();
          } else {
            if (res && res.error_message) {
              this.toastService.showPopup("ERROR", "Error", res.error_message, 3000);
            } else {
              this.toastService.showPopup("ERROR", "Error", "Error while saving data fields.", 3000);
            }
          }
          this.isSaving = false;
        },
        error: (err: any) => {
          console.error('Error saving data fields:', err);
          this.toastService.showPopup("ERROR", "ERROR", "Error while saving data fields.", 3500);
          this.isSaving = false;
        }
      });
  }

  /**
   * Get the latest brand details and update it on helpers and app state
   */
  afterUpdateBrandConfig() {
    this.pagerBusy = true;
    this.accountsService.getBrandDetail(this.brandDetails._id).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((response) => {
      if (response.code === 0 && response.data.brand) {
        this.brandDetails = response.data.brand;
        this.stateService.updateBrandDetails(this.brandDetails);
        // Reset and refresh data field list to reflect deletions
        this.resetData();
        this.getDataFieldList();
        // Also refresh advanced data if in advanced mode
        if (this.viewMode === 'advanced') {
          this.getAdvancedData();
        }
        this.pagerBusy = false;
      } else {
        this.pagerBusy = false;
      }
    });
  }

  /**
   * Cancel the data fields configuration changes
   */
  cancelConfiguration() {
    if (this.isCancelling) return;

    this.isCancelling = true;

    // Reset data to original state by reloading from server
    this.onChange();

    setTimeout(() => {
      if (Helpers && Helpers.smallAlertBox) {
        Helpers.smallAlertBox('Info', 'Data fields settings have been reset', 'info', 3000);
      }
      this.isSaveEnabled = false;
      this.isCancelling = false;
    }, 500);
  }

  /**
   * Show confirmation box
   */
  private showConfirmationBox(title: string, message: string, callback: (answer: boolean) => void) {
    if (Helpers && Helpers.SmartMessageBox) {
      Helpers.SmartMessageBox({
        title: title,
        content: message,
        buttons: [
          { text: "No", type: "secondary", key: "No" },
          { text: "Yes", type: "primary", key: "Yes" }
        ]
      }, (ButtonPressed: string) => {
        if (ButtonPressed === 'Yes') {
          callback(true)
        } else {
          callback(false)
        }
      });
    } else {
      // Fallback
      const answer = confirm(message);
      callback(answer);
    }
  }

  /**
   * Can deactivate guard implementation
   */
  canDeactivate(): Promise<boolean> | boolean {
    const hasUnsavedChanges = this.isSaveEnabled;
    if (hasUnsavedChanges) {
      return this.confirmDiscardChanges();
    }
    return true;
  }

  /**
   * Show confirmation dialog for unsaved changes
   */
  confirmDiscardChanges(): Promise<boolean> {
    return new Promise((resolve) => {
      if (Helpers && Helpers.SmartMessageBox) {
        Helpers.SmartMessageBox(
          {
            title: "You have reordered the rows",
            content: "To save your changes, please use the <b>Save</b> button at the top right of the page. Click <b>Discard Changes</b> to lose your edits and continue. Click <b>Cancel</b> to close this popup and keep editing.",
            buttons: [
              { text: "Cancel", type: "secondary", key: "Cancel" },
              { text: "Discard Changes", type: "primary", key: "Discard" }
            ]
          },
          (ButtonPressed: string) => {
            setTimeout(() => {
              if (ButtonPressed === "Discard") {
                this.isSaveEnabled = false;
                resolve(true);
              } else {
                resolve(false);
              }
            }, 50);
          }
        );
      } else {
        // Fallback if Helpers is not available
        const shouldDiscard = confirm('You have unsaved changes. Are you sure you want to discard them?');
        if (shouldDiscard) {
          this.isSaveEnabled = false;
        }
        resolve(shouldDiscard);
      }
    });
  }
}
